import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import "./ProyectoFinanzasPanel.css";

// PCC_FINANCE_READONLY_GOOGLE_V4_2
// La captura financiera oficial se realiza en Google Sheets.
// El Command Center financiero queda en modo consulta.
const FINANCE_READ_ONLY = true;

const PROYECTOS_API_BASE =
  process.env.REACT_APP_PROYECTOS_API_BASE ||
  "https://g4xylhvnxf.execute-api.us-east-1.amazonaws.com/dev";

const MODELOS_FINANCIEROS = [
  "CONTRATO_FIJO",
  "POR_UNIDAD",
  "MENSUALIDAD",
  "POR_HITOS",
  "MIXTO",
  "OPERACION_CONTINUA",
];

const TIPOS_MOVIMIENTO = ["INGRESO", "COSTO", "AJUSTE"];

const NATURALEZAS = [
  "UNICO",
  "RECURRENTE_MENSUAL",
  "BOLSA_HORAS",
  "POR_UNIDAD",
  "EXTRAORDINARIO",
];

const CATEGORIAS_INGRESO = [
  "COBRO_CLIENTE",
  "CORTE_PRODUCCION",
  "MENSUALIDAD",
  "HITO",
  "SOPORTE",
  "SERVICIO_OPERADO",
  "OTRO_INGRESO",
];

const CATEGORIAS_COSTO = [
  "DESARROLLO",
  "BOLSA_HORAS_DESARROLLO",
  "EQUIPO",
  "ACCESORIOS",
  "LICENCIAS",
  "PERSONAL",
  "OPERADOR",
  "VIATICOS",
  "SOPORTE",
  "CONSUMIBLES",
  "REFACCIONES",
  "INFRAESTRUCTURA",
  "SUBCONTRATACION",
  "GARANTIAS",
  "OTROS",
];

const ESTADOS = [
  "PENDIENTE",
  "PAGADO",
  "FACTURADO",
  "COBRADO",
  "POR_COMPROBAR",
  "COMPROMETIDO",
];

const UNIDADES = [
  "pieza",
  "hora",
  "licencia",
  "tramite",
  "servicio",
  "mes",
  "hito",
  "usuario",
  "estacion",
  "paquete",
  "evento",
];

function money(value) {
  return Number(value || 0).toLocaleString("es-MX", {
    style: "currency",
    currency: "MXN",
    maximumFractionDigits: 2,
  });
}

function percent(value) {
  return Number(value || 0).toFixed(1) + "%";
}

function today() {
  return new Date().toISOString().slice(0, 10);
}

function monthKey() {
  return new Date().toISOString().slice(0, 7);
}

function storageKeyConfig(proyectoId) {
  return "ipdepot.finanzas.config.enterprise." + (proyectoId || "sin-proyecto");
}

function storageKeyMovimientos(proyectoId) {
  return "ipdepot.finanzas.movimientos.enterprise." + (proyectoId || "sin-proyecto");
}

async function finanzasRequest(path, options = {}) {
  const response = await fetch(PROYECTOS_API_BASE + path, {
    ...options,
    headers: {
      "Content-Type": "application/json; charset=utf-8",
      ...(options.headers || {}),
    },
  });

  let payload = null;

  try {
    payload = await response.json();
  } catch {
    payload = null;
  }

  if (!response.ok || payload?.ok === false) {
    const message =
      payload?.message ||
      payload?.error ||
      "No fue posible comunicar con Finanzas backend.";
    const error = new Error(message);
    error.status = response.status;
    error.payload = payload;
    throw error;
  }

  return payload;
}

function backendProjectPath(proyectoId, suffix) {
  return "/proyectos/" + encodeURIComponent(proyectoId) + suffix;
}

function normalizeBackendConfig(item) {
  return {
    ...emptyConfig(),
    ...(item || {}),
    precioUnitario: item?.precioUnitario ?? "",
    montoContrato: item?.montoContrato ?? "",
    mensualidad: item?.mensualidad ?? "",
    metaMaxima: item?.metaMaxima ?? "",
    ivaIncluido: item?.ivaIncluido !== false,
  };
}

function normalizeBackendMovimiento(item) {
  return {
    ...emptyMovimiento(item?.tipo || "COSTO"),
    ...(item || {}),
    monto: item?.monto ?? "",
    cantidad: item?.cantidad ?? "",
    precioUnitario: item?.precioUnitario ?? "",
    concepto: item?.concepto || "",

    numeroFactura: item?.numeroFactura || "",
    clasificacionFinanciera: item?.clasificacionFinanciera || "",
    descripcionBancaria: item?.descripcionBancaria || "",

    proveedor: item?.proveedor || "",
    responsable: item?.responsable || "",
    notas: item?.notas || "",
  };
}

function emptyConfig() {
  return {
    modeloFinanciero: "MIXTO",
    unidadCobro: "servicio",
    precioUnitario: "",
    ivaIncluido: true,
    montoContrato: "",
    mensualidad: "",
    metaMaxima: "",
    notasComerciales: "",
  };
}

function emptyMovimiento(tipo = "COSTO") {
  const isIngreso = tipo === "INGRESO";

  return {
    tipo,
    naturaleza: isIngreso ? "UNICO" : "UNICO",
    categoria: isIngreso ? "COBRO_CLIENTE" : "VIATICOS",
    monto: "",
    periodo: monthKey(),
    fecha: today(),
    cantidad: "",
    unidad: isIngreso ? "servicio" : "pieza",
    precioUnitario: "",
    ivaIncluido: true,
    concepto: "",

    // PCC_FIN_METADATA_V1
    numeroFactura: "",
    clasificacionFinanciera: isIngreso ? "INGRESO_VENTA" : "",
    descripcionBancaria: "",

    proveedor: "",
    responsable: "",
    estado: isIngreso ? "COBRADO" : "PAGADO",
    notas: "",
  };
}

function labelModelo(value) {
  const map = {
    CONTRATO_FIJO: "Contrato fijo",
    POR_UNIDAD: "Por unidad / produccion",
    MENSUALIDAD: "Mensualidad / soporte",
    POR_HITOS: "Por hitos",
    MIXTO: "Mixto",
    OPERACION_CONTINUA: "Operacion continua",
  };

  return map[value] || value;
}

function labelNaturaleza(value) {
  const map = {
    UNICO: "Unico",
    RECURRENTE_MENSUAL: "Recurrente mensual",
    BOLSA_HORAS: "Costo de bolsa de horas",
    POR_UNIDAD: "Por unidad",
    EXTRAORDINARIO: "Extraordinario",
  };

  return map[value] || value;
}

function labelCategoria(value) {
  const map = {
    COBRO_CLIENTE: "Cobro cliente",
    CORTE_PRODUCCION: "Corte produccion",
    MENSUALIDAD: "Mensualidad",
    HITO: "Hito",
    SOPORTE: "Soporte",
    SERVICIO_OPERADO: "Servicio operado",
    OTRO_INGRESO: "Otro ingreso",
    DESARROLLO: "Desarrollo",
    BOLSA_HORAS_DESARROLLO: "Bolsa horas desarrollo",
    EQUIPO: "Equipo",
    ACCESORIOS: "Accesorios",
    LICENCIAS: "Licencias",
    PERSONAL: "Personal",
    OPERADOR: "Operador",
    VIATICOS: "Viaticos",
    CONSUMIBLES: "Consumibles",
    REFACCIONES: "Refacciones",
    INFRAESTRUCTURA: "Infraestructura",
    SUBCONTRATACION: "Subcontratacion",
    GARANTIAS: "Garantias",
    OTROS: "Otros",
  };

  return map[value] || value;
}

function toNumber(value) {
  const n = Number(value || 0);
  return Number.isFinite(n) ? n : 0;
}

// ============================================================
// PCC_COBRO_DIRECTO_V5
// ============================================================

function normalizeDirectMoney(value) {
  const raw = String(value ?? "")
    .replace(/[$,\s]/g, "")
    .replace(/[^\d.]/g, "");

  if (!raw) return "";

  const dotIndex = raw.indexOf(".");

  let integerPart =
    dotIndex >= 0
      ? raw.slice(0, dotIndex)
      : raw;

  let decimalPart =
    dotIndex >= 0
      ? raw
          .slice(dotIndex + 1)
          .replace(/\./g, "")
          .slice(0, 2)
      : "";

  integerPart =
    integerPart
      .replace(/\D/g, "")
      .replace(/^0+(?=\d)/, "") || "0";

  return dotIndex >= 0
    ? integerPart + "." + decimalPart
    : integerPart;
}

function formatDirectMoney(value) {
  const normalized =
    normalizeDirectMoney(value);

  if (!normalized) return "";

  const parts = normalized.split(".");
  const integerPart = parts[0] || "0";
  const decimals = parts[1];

  const formattedInteger =
    Number(integerPart).toLocaleString(
      "en-US",
      {
        maximumFractionDigits: 0,
      }
    );

  return decimals !== undefined
    ? formattedInteger + "." + decimals
    : formattedInteger;
}

function directNumberToSpanish(value) {
  const n =
    Math.floor(Number(value || 0));

  if (n === 0) return "cero";

  const units = [
    "",
    "uno",
    "dos",
    "tres",
    "cuatro",
    "cinco",
    "seis",
    "siete",
    "ocho",
    "nueve",
    "diez",
    "once",
    "doce",
    "trece",
    "catorce",
    "quince",
    "dieciseis",
    "diecisiete",
    "dieciocho",
    "diecinueve",
    "veinte",
    "veintiuno",
    "veintidos",
    "veintitres",
    "veinticuatro",
    "veinticinco",
    "veintiseis",
    "veintisiete",
    "veintiocho",
    "veintinueve",
  ];

  const tens = {
    30: "treinta",
    40: "cuarenta",
    50: "cincuenta",
    60: "sesenta",
    70: "setenta",
    80: "ochenta",
    90: "noventa",
  };

  const hundreds = {
    200: "doscientos",
    300: "trescientos",
    400: "cuatrocientos",
    500: "quinientos",
    600: "seiscientos",
    700: "setecientos",
    800: "ochocientos",
    900: "novecientos",
  };

  if (n < 30) {
    return units[n];
  }

  if (n < 100) {
    const base =
      Math.floor(n / 10) * 10;

    const rest =
      n % 10;

    return rest
      ? tens[base] +
          " y " +
          directNumberToSpanish(rest)
      : tens[base];
  }

  if (n < 1000) {
    if (n === 100) {
      return "cien";
    }

    const base =
      Math.floor(n / 100) * 100;

    const rest =
      n % 100;

    const prefix =
      base === 100
        ? "ciento"
        : hundreds[base];

    return rest
      ? prefix +
          " " +
          directNumberToSpanish(rest)
      : prefix;
  }

  if (n < 1000000) {
    const thousands =
      Math.floor(n / 1000);

    const rest =
      n % 1000;

    const prefix =
      thousands === 1
        ? "mil"
        : directNumberToSpanish(
            thousands
          ) + " mil";

    return rest
      ? prefix +
          " " +
          directNumberToSpanish(rest)
      : prefix;
  }

  if (n < 1000000000) {
    const millions =
      Math.floor(n / 1000000);

    const rest =
      n % 1000000;

    const prefix =
      millions === 1
        ? "un millon"
        : directNumberToSpanish(
            millions
          ) + " millones";

    return rest
      ? prefix +
          " " +
          directNumberToSpanish(rest)
      : prefix;
  }

  if (n < 1000000000000) {
    const billions =
      Math.floor(n / 1000000000);

    const rest =
      n % 1000000000;

    const prefix =
      billions === 1
        ? "mil millones"
        : directNumberToSpanish(
            billions
          ) + " mil millones";

    return rest
      ? prefix +
          " " +
          directNumberToSpanish(rest)
      : prefix;
  }

  return String(n);
}

function directMoneyToWordsMx(value) {
  const amount =
    Number(
      normalizeDirectMoney(value) || 0
    );

  if (
    !Number.isFinite(amount) ||
    amount <= 0
  ) {
    return "";
  }

  let integerPart =
    Math.floor(amount);

  let cents =
    Math.round(
      (amount - integerPart) * 100
    );

  if (cents >= 100) {
    integerPart += 1;
    cents = 0;
  }

  return (
    directNumberToSpanish(integerPart)
      .toUpperCase() +
    (
      integerPart === 1
        ? " PESO "
        : " PESOS "
    ) +
    String(cents).padStart(2, "0") +
    "/100 M.N."
  );
}

// PCC_COBRO_DIRECTO_V5_END
// ============================================================
// PCC_COSTO_DIRECTO_V6B
// Ingreso y costo unico usan captura directa de monto.
// ============================================================

function isDirectMoneyMovement(mov) {
  return (
    mov?.tipo === "INGRESO" ||
    (
      mov?.tipo === "COSTO" &&
      mov?.naturaleza === "UNICO"
    )
  );
}

// PCC_COSTO_DIRECTO_V6B_END
function calcularImporte(mov, config) {
  const cantidad = toNumber(mov.cantidad);
  const precio = toNumber(mov.precioUnitario || config.precioUnitario);
  const monto = toNumber(mov.monto);

  if (mov.tipo === "INGRESO" && monto > 0) {
    return monto;
  }

  if (mov.naturaleza === "POR_UNIDAD") {
    return cantidad * precio;
  }

  if (mov.naturaleza === "BOLSA_HORAS") {
    return monto || cantidad * precio;
  }

  if (mov.categoria === "CORTE_PRODUCCION") {
    return cantidad * precio;
  }

  return monto || cantidad * precio;
}

function clamp(value, min, max) {
  return Math.max(min, Math.min(max, Number(value || 0)));
}

function safeText(value) {
  return String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

function moneyCompact(value) {
  const n = Number(value || 0);
  if (Math.abs(n) >= 1000000) {
    return "$" + (n / 1000000).toFixed(2) + "M";
  }
  if (Math.abs(n) >= 1000) {
    return "$" + (n / 1000).toFixed(1) + "K";
  }
  return money(n);
}

function csvValue(value) {
  const text = String(value ?? "").replace(/"/g, '""');
  return '"' + text + '"';
}

function shortDateTime() {
  return new Date().toISOString().replace(/[-:T]/g, "").slice(0, 12);
}

function classMovimiento(tipo) {
  return String(tipo || "").toLowerCase();
}

export default function ProyectoFinanzasPanel({
  onWorkspaceClose,
 proyecto, visible, workspaceRequest }) {
  const proyectoId = proyecto?.id || "sin-proyecto";

  const [config, setConfig] = useState(emptyConfig());
  const [movimientos, setMovimientos] = useState([]);

  const [modalConfigAbierto, setModalConfigAbierto] = useState(false);
  const [modalMovimientoAbierto, setModalMovimientoAbierto] = useState(false);
  const [modalFinanzasAbierto, setModalFinanzasAbierto] = useState(false);

  // PCC_DIRECT_FINANCE_WORKSPACE_V1
  useEffect(() => {
    if (!visible) return;

    const requestedId = String(
      workspaceRequest?.projectId || ""
    );

    const currentId = String(
      proyecto?.id ||
      proyecto?.proyectoId ||
      ""
    );

    if (!requestedId || !currentId) return;

    if (requestedId !== currentId) return;

    if (!workspaceRequest?.token) return;

    setModalFinanzasAbierto(true);
  }, [
    workspaceRequest?.token,
    workspaceRequest?.projectId,
    proyecto?.id,
    proyecto?.proyectoId,
    visible,
  ]);
  const [financeChartRange, setFinanceChartRange] =
    useState("12M");


  // PCC_FINANCE_REDESIGN_V2
  const [financeCaptureType, setFinanceCaptureType] =
    useState("INGRESO");

  const [financeWorkspaceView, setFinanceWorkspaceView] =
    useState("RESUMEN");

  const [formConfig, setFormConfig] = useState(emptyConfig());
  const [formMovimiento, setFormMovimiento] = useState(emptyMovimiento("COSTO"));
  const [movimientoEditando, setMovimientoEditando] = useState(null);

  // PCC_FINANCE_SOURCE_STATE_V4_2
  const [financeSource, setFinanceSource] = useState("CARGANDO");
  const [financeFallback, setFinanceFallback] = useState(false);
  const [financeLoadError, setFinanceLoadError] = useState("");
  const [financeSyncAt, setFinanceSyncAt] = useState("");

  const [filtrosFinancieros, setFiltrosFinancieros] = useState({
    periodo: "TODOS",
    tipo: "TODOS",
    naturaleza: "TODAS",
    estado: "TODOS",
  });

  useEffect(() => {
    let alive = true;

    async function cargarFinanzas() {
      if (!proyectoId) return;

      let fallbackConfig = emptyConfig();
      let fallbackMovs = [];

      try {
        const rawConfig = localStorage.getItem(storageKeyConfig(proyectoId));
        const parsedConfig = rawConfig ? JSON.parse(rawConfig) : emptyConfig();
        fallbackConfig = { ...emptyConfig(), ...parsedConfig };
      } catch {
        fallbackConfig = emptyConfig();
      }

      try {
        const rawMovs = localStorage.getItem(storageKeyMovimientos(proyectoId));
        const parsedMovs = rawMovs ? JSON.parse(rawMovs) : [];
        fallbackMovs = Array.isArray(parsedMovs) ? parsedMovs : [];
      } catch {
        fallbackMovs = [];
      }

      try {
        const [configResp, movsResp] = await Promise.all([
          finanzasRequest(backendProjectPath(proyectoId, "/finanzas/config")),
          finanzasRequest(backendProjectPath(proyectoId, "/finanzas/movimientos")),
        ]);

        if (!alive) return;

        const nextConfig = normalizeBackendConfig(configResp?.item);
        const nextMovs = Array.isArray(movsResp?.items)
          ? movsResp.items.map(normalizeBackendMovimiento)
          : [];

        setConfig(nextConfig);
        setFormConfig(nextConfig);
        setMovimientos(nextMovs);

        // PCC_FINANCE_SOURCE_LOAD_V4_2
        setFinanceSource(
          String(movsResp?.source || "BACKEND").toUpperCase()
        );

        setFinanceFallback(Boolean(movsResp?.fallback));
        setFinanceLoadError("");
        setFinanceSyncAt(new Date().toISOString());

        localStorage.setItem(storageKeyConfig(proyectoId), JSON.stringify(nextConfig));
        localStorage.setItem(storageKeyMovimientos(proyectoId), JSON.stringify(nextMovs));
      } catch (error) {
        // IP_DEPOT_FIN027B8FR_NO_STALE_FINANCE
        if (!alive) return;

        console.error(
          "IP_DEPOT_GOOGLE_FINANCE_READ_FAILED",
          error?.message || error
        );

        // La fuente oficial falló.
        // Nunca sustituirla con movimientos antiguos.
        setFinanceSource("GOOGLE_SHEETS_ERROR");
        setFinanceFallback(false);
        setFinanceLoadError(
          "No fue posible consultar Google Sheets. No se mostrarán movimientos almacenados localmente."
        );
        setFinanceSyncAt("");

        setConfig(emptyConfig());
        setFormConfig(emptyConfig());
        setMovimientos([]);
      }
    }

    cargarFinanzas();

    return () => {
      alive = false;
    };
  }, [proyectoId]);

  // IP_DEPOT_FIN027B8N_FINAL_FAST_SYNC
  // Actualiza exclusivamente el proyecto financiero visible.
  useEffect(() => {
    if (!visible || !proyectoId || proyectoId === "sin-proyecto") {
      return undefined;
    }

    let active = true;
    let busy = false;

    const refresh = async () => {
      if (!active || busy || document.visibilityState === "hidden") {
        return;
      }

      busy = true;

      try {
        const result = await finanzasRequest(
          backendProjectPath(proyectoId, "/finanzas/movimientos")
        );

        if (!active) return;

        if (
          String(result?.source || "").toUpperCase() !== "GOOGLE_SHEETS" ||
          result?.fallback ||
          !Array.isArray(result?.items)
        ) {
          throw new Error("FIN027B8N_INVALID_GOOGLE_SOURCE");
        }

        setMovimientos(
          result.items.map(normalizeBackendMovimiento)
        );

        setFinanceSource("GOOGLE_SHEETS");
        setFinanceFallback(false);
        setFinanceLoadError("");
        setFinanceSyncAt(new Date().toISOString());

      } catch (error) {
        if (!active) return;

        setMovimientos([]);
        setFinanceSource("GOOGLE_SHEETS");
        setFinanceFallback(false);
        setFinanceSyncAt("");

        setFinanceLoadError(
          "No fue posible actualizar Google Sheets: " +
          (error?.message || "Error de sincronizacion")
        );

      } finally {
        busy = false;
      }
    };

    const timer = window.setInterval(refresh, 15000);

    const onVisibility = () => {
      if (document.visibilityState === "visible") {
        void refresh();
      }
    };

    document.addEventListener(
      "visibilitychange",
      onVisibility
    );

    return () => {
      active = false;
      window.clearInterval(timer);

      document.removeEventListener(
        "visibilitychange",
        onVisibility
      );
    };
  }, [proyectoId, visible]);


  async function persistirConfig(next) {
    const normalizado = {
      ...emptyConfig(),
      ...next,
      precioUnitario: toNumber(next.precioUnitario),
      montoContrato: toNumber(next.montoContrato),
      mensualidad: toNumber(next.mensualidad),
      metaMaxima: toNumber(next.metaMaxima),
      ivaIncluido: Boolean(next.ivaIncluido),
    };

    try {
      const resp = await finanzasRequest(
        backendProjectPath(proyectoId, "/finanzas/config"),
        {
          method: "PUT",
          body: JSON.stringify(normalizado),
        }
      );

      const backendConfig = normalizeBackendConfig(resp?.item || normalizado);

      setConfig(backendConfig);
      setFormConfig(backendConfig);
      localStorage.setItem(storageKeyConfig(proyectoId), JSON.stringify(backendConfig));

      return backendConfig;
    } catch (error) {
      console.warn("No se pudo guardar config en backend. Guardando fallback local.", error);

      setConfig(normalizado);
      setFormConfig(normalizado);
      localStorage.setItem(storageKeyConfig(proyectoId), JSON.stringify(normalizado));

      alert("No se pudo guardar en backend. Se guardó temporalmente en este navegador.");
      return normalizado;
    }
  }

  function persistirMovimientos(next) {
    setMovimientos(next);
    localStorage.setItem(storageKeyMovimientos(proyectoId), JSON.stringify(next));
  }

  const periodosDisponibles = useMemo(() => {
    const periodos = movimientos
      .map((mov) => mov.periodo || String(mov.fecha || "").slice(0, 7))
      .filter(Boolean);

    return Array.from(new Set(periodos)).sort((a, b) => String(b).localeCompare(String(a)));
  }, [movimientos]);

  const movimientosFiltrados = useMemo(() => {
    return movimientos.filter((mov) => {
      const periodoMov = mov.periodo || String(mov.fecha || "").slice(0, 7);

      const pasaPeriodo =
        filtrosFinancieros.periodo === "TODOS" ||
        periodoMov === filtrosFinancieros.periodo;

      const pasaTipo =
        filtrosFinancieros.tipo === "TODOS" ||
        mov.tipo === filtrosFinancieros.tipo;

      const pasaNaturaleza =
        filtrosFinancieros.naturaleza === "TODAS" ||
        mov.naturaleza === filtrosFinancieros.naturaleza;

      const pasaEstado =
        filtrosFinancieros.estado === "TODOS" ||
        mov.estado === filtrosFinancieros.estado;

      return pasaPeriodo && pasaTipo && pasaNaturaleza && pasaEstado;
    });
  }, [movimientos, filtrosFinancieros]);

  function actualizarFiltroFinanciero(campo, valor) {
    setFiltrosFinancieros((prev) => ({
      ...prev,
      [campo]: valor,
    }));
  }

  function limpiarFiltrosFinancieros() {
    setFiltrosFinancieros({
      periodo: "TODOS",
      tipo: "TODOS",
      naturaleza: "TODAS",
      estado: "TODOS",
    });
  }

  const hayFiltrosActivos =
    filtrosFinancieros.periodo !== "TODOS" ||
    filtrosFinancieros.tipo !== "TODOS" ||
    filtrosFinancieros.naturaleza !== "TODAS" ||
    filtrosFinancieros.estado !== "TODOS";

  const finanzas = useMemo(() => {
    const contratoBase = toNumber(
      config.montoContrato ||
      proyecto?.montoContratado ||
      proyecto?.montoTotal ||
      proyecto?.valorContrato ||
      0
    );

    const esperadoContrato = contratoBase;
    const esperadoMensualidad = toNumber(config.mensualidad);
    const esperadoProduccion = toNumber(config.metaMaxima) * toNumber(config.precioUnitario);

    let ingresoEsperado = esperadoContrato;

    if (config.modeloFinanciero === "POR_UNIDAD") {
      ingresoEsperado = esperadoProduccion;
    } else if (config.modeloFinanciero === "MENSUALIDAD") {
      ingresoEsperado = esperadoMensualidad;
    } else if (config.modeloFinanciero === "MIXTO" || config.modeloFinanciero === "OPERACION_CONTINUA") {
      ingresoEsperado = esperadoContrato + esperadoMensualidad + esperadoProduccion;
    }

    const ingresos = movimientosFiltrados
      .filter((m) => m.tipo === "INGRESO")
      .reduce((acc, m) => acc + calcularImporte(m, config), 0);

    const costos = movimientosFiltrados
      .filter((m) => m.tipo === "COSTO")
      .reduce((acc, m) => acc + calcularImporte(m, config), 0);

    const ajustes = movimientosFiltrados
      .filter((m) => m.tipo === "AJUSTE")
      .reduce((acc, m) => acc + calcularImporte(m, config), 0);

    // PCC_FIN_COSTO_NETO_ROI_V1_3
    const costoBruto = costos;

    // PCC_FIN_COST_CLASSIFICATION_V1_2
    const costoDirectoGeneral = movimientosFiltrados
      .filter(
        (m) =>
          m.tipo === "COSTO" &&
          String(m.clasificacionFinanciera || "").toUpperCase() ===
            "COSTO_DIRECTO_GENERAL"
      )
      .reduce((acc, m) => acc + calcularImporte(m, config), 0);

    const costoDirectoEspecifico = movimientosFiltrados
      .filter(
        (m) =>
          m.tipo === "COSTO" &&
          String(m.clasificacionFinanciera || "").toUpperCase() ===
            "COSTO_DIRECTO_ESPECIFICO"
      )
      .reduce((acc, m) => acc + calcularImporte(m, config), 0);

    const costoIndirecto = movimientosFiltrados
      .filter(
        (m) =>
          m.tipo === "COSTO" &&
          String(m.clasificacionFinanciera || "").toUpperCase() ===
            "COSTO_INDIRECTO"
      )
      .reduce((acc, m) => acc + calcularImporte(m, config), 0);

    const costoDirecto =
      costoDirectoGeneral +
      costoDirectoEspecifico;

    const porcentajeCostoDirecto =
      costoBruto > 0
        ? (costoDirecto / costoBruto) * 100
        : 0;

    const porcentajeCostoIndirecto =
      costoBruto > 0
        ? (costoIndirecto / costoBruto) * 100
        : 0;

    const costoNeto = Math.max(0, costoBruto - ajustes);

    // Se mantiene para compatibilidad interna.
    const invertido = costoBruto;

    const utilidad = ingresos - costoBruto + ajustes;
    const margen = ingresos > 0 ? (utilidad / ingresos) * 100 : 0;

    const roi =
      costoNeto > 0
        ? (utilidad / costoNeto) * 100
        : 0;

    const porCobrar = Math.max(0, ingresoEsperado - ingresos);
    const avanceCobro = ingresoEsperado > 0 ? (ingresos / ingresoEsperado) * 100 : 0;

    const unidades = movimientosFiltrados
      .filter((m) => m.tipo === "INGRESO" && (m.categoria === "CORTE_PRODUCCION" || m.naturaleza === "POR_UNIDAD"))
      .reduce((acc, m) => acc + toNumber(m.cantidad), 0);

    const horasBolsa = movimientosFiltrados
      .filter((m) => m.tipo === "COSTO" && m.naturaleza === "BOLSA_HORAS")
      .reduce((acc, m) => acc + toNumber(m.cantidad), 0);

    const costoBolsaHoras = movimientosFiltrados
      .filter((m) => m.tipo === "COSTO" && m.naturaleza === "BOLSA_HORAS")
      .reduce((acc, m) => acc + calcularImporte(m, config), 0);

    const recurrenteMensual = movimientosFiltrados
      .filter((m) => m.tipo === "COSTO" && m.naturaleza === "RECURRENTE_MENSUAL")
      .reduce((acc, m) => acc + calcularImporte(m, config), 0);

    const extraordinarios = movimientosFiltrados
      .filter((m) => m.tipo === "COSTO" && m.naturaleza === "EXTRAORDINARIO")
      .reduce((acc, m) => acc + calcularImporte(m, config), 0);

    const costoPorUnidad = unidades > 0 ? invertido / unidades : 0;
    const ingresoPorUnidad = unidades > 0 ? ingresos / unidades : toNumber(config.precioUnitario);
    const utilidadPorUnidad = ingresoPorUnidad - costoPorUnidad;
    const costoHoraPromedio = horasBolsa > 0 ? costoBolsaHoras / horasBolsa : 0;

    const costosPorCategoria = {};
    CATEGORIAS_COSTO.forEach((cat) => {
      costosPorCategoria[cat] = 0;
    });

    movimientosFiltrados
      .filter((m) => m.tipo === "COSTO")
      .forEach((m) => {
        const cat = costosPorCategoria[m.categoria] !== undefined ? m.categoria : "OTROS";
        costosPorCategoria[cat] += calcularImporte(m, config);
      });

    const desgloseCostos = CATEGORIAS_COSTO.map((cat) => ({
      key: cat,
      label: labelCategoria(cat),
      value: costosPorCategoria[cat] || 0,
    })).filter((item) => item.value > 0);

    let estado = "Sin datos financieros";
    let estadoClase = "neutral";

    if (ingresos <= 0 && invertido > 0) {
      estado = "Inversion sin recuperacion";
      estadoClase = "danger";
    } else if (margen < 0) {
      estado = "Perdida";
      estadoClase = "danger";
    } else if (margen < 15 && ingresos > 0) {
      estado = "Riesgo financiero";
      estadoClase = "warning";
    } else if (margen < 30 && ingresos > 0) {
      estado = "Atencion";
      estadoClase = "attention";
    } else if (margen >= 30) {
      estado = "Rentable";
      estadoClase = "healthy";
    }

    return {
      ingresoEsperado,
      ingresos,
      porCobrar,
      invertido,
      costoBruto,
      costoDirectoGeneral,
      costoDirectoEspecifico,
      costoDirecto,
      costoIndirecto,
      porcentajeCostoDirecto,
      porcentajeCostoIndirecto,
      ajustes,
      costoNeto,
      roi,
      utilidad,
      margen,
      avanceCobro,
      unidades,
      costoPorUnidad,
      ingresoPorUnidad,
      utilidadPorUnidad,
      horasBolsa,
      costoBolsaHoras,
      costoHoraPromedio,
      recurrenteMensual,
      extraordinarios,
      desgloseCostos,
      estado,
      estadoClase,
    };
  }, [proyecto, config, movimientosFiltrados]);

  function bloquearEscrituraFinanciera() {
    if (!FINANCE_READ_ONLY) {
      return false;
    }

    window.alert(
      "Finanzas está en modo consulta. " +
      "La captura oficial se realiza en Google Sheets."
    );

    return true;
  }

  function abrirConfig() {
    if (bloquearEscrituraFinanciera()) return;
    setFormConfig({ ...emptyConfig(), ...config });
    setModalConfigAbierto(true);
  }

  function actualizarConfig(campo, valor) {
    setFormConfig((prev) => ({
      ...prev,
      [campo]: campo === "ivaIncluido" ? valor === "true" : valor,
    }));
  }

  async function guardarConfig() {
    if (bloquearEscrituraFinanciera()) return;
    await persistirConfig(formConfig);
    setModalConfigAbierto(false);
  }

  function abrirMovimiento(tipo, preset = {}) {
    if (bloquearEscrituraFinanciera()) return;
    setMovimientoEditando(null);

    const next = {
      ...emptyMovimiento(tipo),
      ...preset,
    };

    if (tipo === "INGRESO" && config.modeloFinanciero === "POR_UNIDAD") {
      next.categoria = "CORTE_PRODUCCION";
      next.naturaleza = "POR_UNIDAD";
      next.unidad = config.unidadCobro || "licencia";
      next.precioUnitario = config.precioUnitario || "";
      next.ivaIncluido = config.ivaIncluido;
    }

    setFormMovimiento(next);
    setModalMovimientoAbierto(true);
  }

  function cerrarModalMovimiento() {
    setModalMovimientoAbierto(false);
    setMovimientoEditando(null);
    setFormMovimiento(emptyMovimiento("COSTO"));
  }

  function abrirEditarMovimiento(movimiento) {
    if (bloquearEscrituraFinanciera()) return;
    if (!movimiento) return;

    setMovimientoEditando(movimiento);
    setFormMovimiento({
      ...emptyMovimiento(movimiento.tipo || "COSTO"),
      ...movimiento,
      monto: movimiento.monto ?? "",
      cantidad: movimiento.cantidad ?? "",
      precioUnitario: movimiento.precioUnitario ?? "",
      concepto: movimiento.concepto || "",
      proveedor: movimiento.proveedor || "",
      responsable: movimiento.responsable || "",
      notas: movimiento.notas || "",
      ivaIncluido: movimiento.ivaIncluido !== false,
    });
    setModalMovimientoAbierto(true);
  }

  function actualizarMovimiento(campo, valor) {
    setFormMovimiento((prev) => {
      const next = {
        ...prev,
        [campo]: campo === "ivaIncluido" ? valor === "true" : valor,
      };

      if (campo === "tipo") {
        if (valor === "INGRESO") {
          next.categoria = "COBRO_CLIENTE";
          next.estado = "COBRADO";
        } else if (valor === "COSTO") {
          next.categoria = "VIATICOS";
          next.estado = "PAGADO";
        } else {
          next.categoria = "OTROS";
          next.estado = "PENDIENTE";
        }
      }

      if (campo === "naturaleza" && valor === "BOLSA_HORAS") {
        next.categoria = "BOLSA_HORAS_DESARROLLO";
        next.unidad = "hora";
      }

      if (campo === "categoria" && valor === "CORTE_PRODUCCION") {
        next.naturaleza = "POR_UNIDAD";
        next.unidad = config.unidadCobro || "licencia";
        next.precioUnitario = next.precioUnitario || config.precioUnitario || "";
      }

      if (campo === "categoria" && valueIsMonthly(valor)) {
        next.naturaleza = "RECURRENTE_MENSUAL";
        next.unidad = "mes";
      }

      return next;
    });
  }

  function valueIsMonthly(value) {
    return value === "MENSUALIDAD" || value === "OPERADOR";
  }
  // =========================================================
  // PCC_FINANCE_ADAPTIVE_CAPTURE_V1_1
  // Presentacion adaptativa.
  // NO modifica guardarMovimiento ni persistencia.
  // =========================================================

  function financeCaptureMode() {
    const tipo = String(formMovimiento?.tipo || "")
      .trim()
      .toUpperCase();

    const naturaleza = String(formMovimiento?.naturaleza || "")
      .trim()
      .toUpperCase();

    if (tipo === "INGRESO") {
      return "ingreso";
    }

    if (naturaleza === "BOLSA_HORAS") {
      return "bolsa";
    }

    if (naturaleza === "RECURRENTE_MENSUAL") {
      return "mensual";
    }

    return "gasto";
  }

  function financeCaptureTitle() {
    const mode = financeCaptureMode();

    if (mode === "ingreso") {
      return movimientoEditando
        ? "Editar ingreso"
        : "Registrar ingreso";
    }

    if (mode === "bolsa") {
      return movimientoEditando
        ? "Editar bolsa de horas"
        : "Registrar bolsa de horas";
    }

    if (mode === "mensual") {
      return movimientoEditando
        ? "Editar costo mensual"
        : "Registrar costo mensual";
    }

    return movimientoEditando
      ? "Editar gasto puntual"
      : "Registrar costo real / gasto puntual";
  }

  async function guardarMovimiento() {
    if (bloquearEscrituraFinanciera()) return;
    const importe = calcularImporte(formMovimiento, config);

    if (!importe || importe <= 0) {
      alert(
        formMovimiento.tipo === "INGRESO"
          ? "Captura un monto total de cobro valido."
          : isDirectMoneyMovement(formMovimiento)
            ? "Captura un monto total del costo valido."
            : "Captura un monto valido, o cantidad y precio unitario validos."
      );
      return;
    }

    if (!String(formMovimiento.concepto || "").trim()) {
      alert("Captura el concepto del movimiento.");
      return;
    }

    const now = new Date().toISOString();
    const normalizado = {
      proyectoId,
      ...formMovimiento,
      id: movimientoEditando?.id || "FIN-" + Date.now(),
      monto: importe,
      cantidad: toNumber(formMovimiento.cantidad),
      precioUnitario: toNumber(formMovimiento.precioUnitario),
      concepto: String(formMovimiento.concepto || "").trim(),

      numeroFactura: String(
        formMovimiento.numeroFactura || ""
      ).trim(),

      clasificacionFinanciera: String(
        formMovimiento.clasificacionFinanciera || ""
      ).trim(),

      descripcionBancaria: String(
        formMovimiento.descripcionBancaria || ""
      ).trim(),

      proveedor: String(formMovimiento.proveedor || "").trim(),
      responsable: String(formMovimiento.responsable || "").trim(),
      notas: String(formMovimiento.notas || "").trim(),
      createdAt: movimientoEditando?.createdAt || now,
      updatedAt: movimientoEditando ? now : undefined,
    };

    try {
      let resp;

      if (movimientoEditando?.id) {
        resp = await finanzasRequest(
          backendProjectPath(
            proyectoId,
            "/finanzas/movimientos/" + encodeURIComponent(movimientoEditando.id)
          ),
          {
            method: "PUT",
            body: JSON.stringify(normalizado),
          }
        );
      } else {
        resp = await finanzasRequest(
          backendProjectPath(proyectoId, "/finanzas/movimientos"),
          {
            method: "POST",
            body: JSON.stringify(normalizado),
          }
        );
      }

      const backendMov = normalizeBackendMovimiento(resp?.item || normalizado);

      if (movimientoEditando?.id) {
        persistirMovimientos(
          movimientos.map((mov) =>
            mov.id === movimientoEditando.id ? backendMov : mov
          )
        );
      } else {
        persistirMovimientos([backendMov, ...movimientos]);
      }

      cerrarModalMovimiento();
    } catch (error) {
      console.warn("No se pudo guardar movimiento en backend. Guardando fallback local.", error);

      if (movimientoEditando?.id) {
        persistirMovimientos(
          movimientos.map((mov) =>
            mov.id === movimientoEditando.id ? normalizado : mov
          )
        );
      } else {
        persistirMovimientos([normalizado, ...movimientos]);
      }

      cerrarModalMovimiento();
      alert("No se pudo guardar en backend. Se guardó temporalmente en este navegador.");
    }
  }

  async function eliminarMovimiento(id) {
    if (bloquearEscrituraFinanciera()) return;
    const ok = window.confirm("Eliminar este movimiento financiero?");
    if (!ok) return;

    try {
      await finanzasRequest(
        backendProjectPath(
          proyectoId,
          "/finanzas/movimientos/" + encodeURIComponent(id)
        ),
        {
          method: "DELETE",
        }
      );

      persistirMovimientos(movimientos.filter((m) => m.id !== id));
    } catch (error) {
      console.warn("No se pudo eliminar movimiento en backend. Eliminando fallback local.", error);

      persistirMovimientos(movimientos.filter((m) => m.id !== id));
      alert("No se pudo eliminar en backend. Se eliminó temporalmente en este navegador.");
    }
  }

  function construirReporteDirectivoHtml() {
    const proyectoNombre = proyecto?.nombre || proyecto?.titulo || "Proyecto";
    const clienteNombre = proyecto?.cliente || proyecto?.clienteNombre || "Sin cliente";
    const periodoVista =
      filtrosFinancieros?.periodo && filtrosFinancieros.periodo !== "TODOS"
        ? filtrosFinancieros.periodo
        : "Vista general";

    const fechaGeneracion = new Date().toLocaleString("es-MX", {
      dateStyle: "medium",
      timeStyle: "short",
    });

    const estadoTexto =
      finanzas.utilidad > 0
        ? "Sí dejó utilidad"
        : finanzas.utilidad < 0
          ? "No está dejando utilidad"
          : "Sin utilidad";

    const mensajeClave =
      finanzas.utilidad > 0 && finanzas.margen >= 30
        ? "El proyecto está generando utilidad sólida."
        : finanzas.utilidad > 0
          ? "El proyecto deja utilidad, pero requiere vigilancia."
          : finanzas.utilidad < 0
            ? "El proyecto no está recuperando la inversión."
            : "El proyecto está en punto de equilibrio o sin datos suficientes.";

    const tono =
      finanzas.estadoClase === "healthy"
        ? { color: "#166534", soft: "#ecfdf3", line: "#22c55e" }
        : finanzas.estadoClase === "danger"
          ? { color: "#991b1b", soft: "#fef2f2", line: "#ef4444" }
          : { color: "#92400e", soft: "#fffbeb", line: "#f59e0b" };

    const avanceCobro = clamp(finanzas.avanceCobro, 0, 100);
    const margen = clamp(Math.abs(finanzas.margen), 0, 100);

    const baseGrafica = Math.max(
      finanzas.ingresos,
      finanzas.costoNeto,
      Math.abs(finanzas.utilidad),
      finanzas.porCobrar,
      1
    );

    const topCostos = [...(finanzas.desgloseCostos || [])]
      .sort((a, b) => b.value - a.value)
      .slice(0, 6);

    const costoRows =
      topCostos.length > 0
        ? topCostos
            .map((item) => {
              const width = clamp((item.value / Math.max(finanzas.invertido, 1)) * 100, 0, 100);
              return `
                <div class="cost-row">
                  <div class="cost-row-top">
                    <strong>${safeText(item.label)}</strong>
                    <span>${money(item.value)}</span>
                  </div>
                  <div class="bar-track">
                    <div class="bar-fill" style="width:${width}%;"></div>
                  </div>
                </div>
              `;
            })
            .join("")
        : `
          <div class="empty-box">
            No hay costos registrados para esta vista.
          </div>
        `;

    const movimientosTop = [...movimientosFiltrados]
      .sort((a, b) => Math.abs(toNumber(b.monto)) - Math.abs(toNumber(a.monto)))
      .slice(0, 5);

    const movimientosImpacto =
      movimientosTop.length > 0
        ? movimientosTop
            .map((mov) => `
              <div class="impact-item ${classMovimiento(mov.tipo)}">
                <div class="impact-main">
                  <span class="impact-type">${safeText(mov.tipo)}</span>
                  <strong>${safeText(mov.concepto || "Movimiento financiero")}</strong>
                  <small>${safeText(labelCategoria(mov.categoria))} · ${safeText(mov.periodo || mov.fecha || "-")}</small>
                </div>
                <div class="impact-amount">${money(toNumber(mov.monto))}</div>
              </div>
            `)
            .join("")
        : `
          <div class="empty-box">
            No hay movimientos relevantes para mostrar.
          </div>
        `;

    const filtrosActivosTexto = [
      filtrosFinancieros?.periodo !== "TODOS" ? "Periodo: " + filtrosFinancieros.periodo : null,
      filtrosFinancieros?.tipo !== "TODOS" ? "Tipo: " + filtrosFinancieros.tipo : null,
      filtrosFinancieros?.naturaleza !== "TODAS" ? "Naturaleza: " + labelNaturaleza(filtrosFinancieros.naturaleza) : null,
      filtrosFinancieros?.estado !== "TODOS" ? "Estado: " + filtrosFinancieros.estado : null,
    ]
      .filter(Boolean)
      .join(" · ");

    return `<!doctype html>
<html lang="es">
<head>
<meta charset="utf-8" />
<meta name="viewport" content="width=device-width,initial-scale=1" />
<title>Reporte Directivo - ${safeText(proyectoNombre)}</title>
<style>
  @page {
    size: landscape;
    margin: 12mm;
  }

  * { box-sizing: border-box; }
  html, body {
    margin: 0;
    padding: 0;
    font-family: Inter, Arial, Helvetica, sans-serif;
    background: #f4f6f8;
    color: #0f172a;
  }

  body {
    padding: 24px;
  }

  .report {
    max-width: 1480px;
    margin: 0 auto;
  }

  .header {
    display: grid;
    grid-template-columns: 1.4fr 1fr;
    gap: 18px;
    margin-bottom: 18px;
  }

  .hero, .summary-card, .panel {
    background: #ffffff;
    border: 1px solid #dbe2ea;
    border-radius: 18px;
    box-shadow: 0 10px 24px rgba(15, 23, 42, 0.06);
  }

  .hero {
    padding: 22px;
  }

  .eyebrow {
    font-size: 11px;
    letter-spacing: .10em;
    text-transform: uppercase;
    color: #64748b;
    margin-bottom: 8px;
    font-weight: 700;
  }

  .hero h1 {
    margin: 0 0 8px;
    font-size: 30px;
    line-height: 1.05;
  }

  .hero p {
    margin: 0;
    color: #475569;
    font-size: 14px;
  }

  .hero-meta {
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
    margin-top: 14px;
  }

  .meta-pill {
    padding: 8px 12px;
    border-radius: 999px;
    background: #f8fafc;
    border: 1px solid #e2e8f0;
    font-size: 12px;
    color: #334155;
    font-weight: 600;
  }

  .status-band {
    margin-top: 16px;
    padding: 16px 18px;
    border-radius: 16px;
    background: ${tono.soft};
    border-left: 6px solid ${tono.line};
    display: grid;
    grid-template-columns: 1.1fr auto;
    gap: 12px;
    align-items: center;
  }

  .status-band .status-text strong {
    display: block;
    font-size: 26px;
    color: ${tono.color};
    line-height: 1.05;
    margin-bottom: 4px;
  }

  .status-band .status-text span {
    display: block;
    color: #475569;
    font-size: 13px;
  }

  .ring {
    --value: ${margen};
    width: 120px;
    height: 120px;
    border-radius: 999px;
    background:
      conic-gradient(${tono.line} calc(var(--value) * 1%), #e5e7eb 0);
    display: flex;
    align-items: center;
    justify-content: center;
    position: relative;
  }

  .ring::after {
    content: "";
    width: 82px;
    height: 82px;
    border-radius: 999px;
    background: #ffffff;
    position: absolute;
  }

  .ring-center {
    position: relative;
    z-index: 1;
    text-align: center;
  }

  .ring-center strong {
    display: block;
    font-size: 23px;
    line-height: 1;
  }

  .ring-center span {
    display: block;
    font-size: 11px;
    color: #64748b;
    margin-top: 4px;
  }

  .summary-card {
    padding: 20px;
    display: grid;
    grid-template-rows: auto 1fr;
  }

  .summary-title {
    margin-bottom: 14px;
  }

  .summary-title strong {
    font-size: 18px;
    display: block;
  }

  .summary-grid {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 10px;
  }

  .summary-kpi {
    border: 1px solid #e2e8f0;
    background: #f8fafc;
    border-radius: 14px;
    padding: 12px;
  }

  .summary-kpi span {
    display: block;
    font-size: 11px;
    color: #64748b;
    text-transform: uppercase;
    letter-spacing: .06em;
    margin-bottom: 6px;
    font-weight: 700;
  }

  .summary-kpi strong {
    display: block;
    font-size: 22px;
    line-height: 1.05;
  }

  .summary-kpi small {
    display: block;
    margin-top: 6px;
    font-size: 11px;
    color: #475569;
  }

  .kpis {
    display: grid;
    grid-template-columns: repeat(6, minmax(0, 1fr));
    gap: 12px;
    margin-bottom: 18px;
  }

  .kpi-card {
    background: #ffffff;
    border: 1px solid #dbe2ea;
    border-radius: 16px;
    padding: 14px;
  }

  .kpi-card span {
    display: block;
    font-size: 11px;
    text-transform: uppercase;
    letter-spacing: .06em;
    color: #64748b;
    margin-bottom: 8px;
    font-weight: 700;
  }

  .kpi-card strong {
    display: block;
    font-size: 24px;
    line-height: 1.05;
  }

  .kpi-card small {
    display: block;
    margin-top: 6px;
    font-size: 12px;
    color: #475569;
  }

  .layout {
    display: grid;
    grid-template-columns: 1.15fr .95fr;
    gap: 18px;
    margin-bottom: 18px;
  }

  .panel {
    padding: 18px;
  }

  .panel h3 {
    margin: 0 0 4px;
    font-size: 18px;
  }

  .panel p.sub {
    margin: 0 0 16px;
    color: #64748b;
    font-size: 12px;
  }

  .compare-bars {
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 12px;
    align-items: end;
    min-height: 260px;
  }

  .compare-item {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 8px;
  }

  .bar-column {
    width: 100%;
    max-width: 110px;
    height: 180px;
    background: #f1f5f9;
    border: 1px solid #e2e8f0;
    border-radius: 14px;
    display: flex;
    align-items: flex-end;
    padding: 8px;
  }

  .bar-fill-vertical {
    width: 100%;
    border-radius: 10px;
  }

  .bar-ingresos { background: linear-gradient(180deg, #86efac, #16a34a); }
  .bar-costos { background: linear-gradient(180deg, #fca5a5, #dc2626); }
  .bar-utilidad { background: linear-gradient(180deg, #93c5fd, #2563eb); }
  .bar-cobrar { background: linear-gradient(180deg, #fde68a, #d97706); }

  .compare-item strong {
    font-size: 14px;
    text-align: center;
  }

  .compare-item span {
    font-size: 12px;
    color: #64748b;
    text-align: center;
  }

  .compare-item small {
    font-size: 12px;
    font-weight: 800;
  }

  .cost-row {
    margin-bottom: 14px;
  }

  .cost-row-top {
    display: flex;
    justify-content: space-between;
    gap: 12px;
    margin-bottom: 7px;
    font-size: 13px;
  }

  .cost-row-top strong {
    font-size: 13px;
  }

  .bar-track {
    width: 100%;
    height: 14px;
    background: #eef2f7;
    border-radius: 999px;
    overflow: hidden;
    border: 1px solid #dbe2ea;
  }

  .bar-fill {
    height: 100%;
    border-radius: 999px;
    background: linear-gradient(90deg, #2563eb, #0f172a);
  }

  .ops {
    display: grid;
    grid-template-columns: repeat(5, minmax(0, 1fr));
    gap: 12px;
    margin-bottom: 18px;
  }

  .ops-card {
    background: #ffffff;
    border: 1px solid #dbe2ea;
    border-radius: 15px;
    padding: 14px;
  }

  .ops-card span {
    display: block;
    font-size: 11px;
    text-transform: uppercase;
    letter-spacing: .06em;
    color: #64748b;
    margin-bottom: 8px;
    font-weight: 700;
  }

  .ops-card strong {
    display: block;
    font-size: 20px;
  }

  .ops-card small {
    display: block;
    margin-top: 6px;
    font-size: 11px;
    color: #475569;
  }

  .impact-panel {
    margin-top: 0;
  }

  .impact-list {
    display: grid;
    gap: 10px;
  }

  .impact-item {
    display: grid;
    grid-template-columns: 1fr auto;
    gap: 12px;
    border: 1px solid #e2e8f0;
    background: #ffffff;
    border-radius: 14px;
    padding: 12px 14px;
    align-items: center;
  }

  .impact-item.ingreso { border-left: 5px solid #16a34a; }
  .impact-item.costo { border-left: 5px solid #dc2626; }
  .impact-item.ajuste { border-left: 5px solid #2563eb; }

  .impact-type {
    display: inline-block;
    font-size: 10px;
    font-weight: 800;
    letter-spacing: .07em;
    text-transform: uppercase;
    color: #64748b;
    margin-bottom: 4px;
  }

  .impact-main strong {
    display: block;
    font-size: 14px;
    line-height: 1.2;
    margin-bottom: 4px;
  }

  .impact-main small {
    color: #64748b;
    font-size: 11px;
  }

  .impact-amount {
    font-size: 16px;
    font-weight: 900;
    white-space: nowrap;
  }

  .empty-box {
    border: 1px dashed #cbd5e1;
    background: #f8fafc;
    border-radius: 14px;
    padding: 18px;
    font-size: 13px;
    color: #64748b;
    text-align: center;
  }

  .footer-note {
    margin-top: 10px;
    background: #ffffff;
    border: 1px solid #dbe2ea;
    border-radius: 16px;
    padding: 14px 16px;
    font-size: 12px;
    color: #475569;
  }

  .footer-note strong {
    color: #0f172a;
  }

  .print-actions {
    display: flex;
    justify-content: flex-end;
    gap: 10px;
    margin-bottom: 16px;
  }

  .print-actions button {
    border: 1px solid #cbd5e1;
    background: #ffffff;
    color: #111827;
    border-radius: 10px;
    padding: 10px 14px;
    cursor: pointer;
    font-weight: 700;
  }

  .print-actions .primary {
    background: #0f172a;
    color: #ffffff;
    border-color: #0f172a;
  }

  .print-actions .primary::before {
    content: "↓ ";
    font-weight: 900;
  }

  .pdf-note {
    margin: -6px 0 16px;
    padding: 10px 14px;
    background: #ffffff;
    border: 1px solid #dbe2ea;
    border-radius: 12px;
    color: #475569;
    font-size: 12px;
  }

  .pdf-note strong {
    color: #0f172a;
  }

  @media print {
    body {
      background: #ffffff;
      padding: 0;
    }

    .print-actions {
      display: none !important;
    }

    .hero, .summary-card, .panel, .kpi-card, .ops-card, .footer-note {
      box-shadow: none;
    }
  }
</style>
</head>
<body>
  <div class="report">
    <div class="print-actions">
      <button class="primary" onclick="window.print(); return false;">Descargar PDF</button>
      <button onclick="window.print(); return false;">Imprimir / guardar PDF</button>
      <button onclick="descargarHtml()">Descargar HTML</button>
      <button onclick="window.close()">Cerrar</button>
    </div>

    <div class="pdf-note">
      <strong>Descargar PDF:</strong> al presionar el botón debe abrirse la ventana de impresión. En destino selecciona <strong>Guardar como PDF</strong>.
    </div>

    <section class="header">
      <div class="hero">
        <div class="eyebrow">Reporte directivo financiero</div>
        <h1>${safeText(proyectoNombre)}</h1>
        <p>${safeText(mensajeClave)}</p>

        <div class="hero-meta">
          <div class="meta-pill">Cliente: ${safeText(clienteNombre)}</div>
          <div class="meta-pill">Periodo: ${safeText(periodoVista)}</div>
          <div class="meta-pill">Modelo: ${safeText(labelModelo(config.modeloFinanciero))}</div>
          <div class="meta-pill">Generado: ${safeText(fechaGeneracion)}</div>
        </div>

        <div class="status-band">
          <div class="status-text">
            <strong>${safeText(estadoTexto)}</strong>
            <span>${safeText(finanzas.estado)} · ${safeText(mensajeClave)}</span>
          </div>

          <div class="ring">
            <div class="ring-center">
              <strong>${percent(finanzas.margen)}</strong>
              <span>Margen</span>
            </div>
          </div>
        </div>
      </div>

      <div class="summary-card">
        <div class="summary-title">
          <div class="eyebrow">Lectura rápida</div>
          <strong>¿Qué pasó con el dinero?</strong>
        </div>

        <div class="summary-grid">
          <div class="summary-kpi">
            <span>Ingresó</span>
            <strong>${money(finanzas.ingresos)}</strong>
            <small>Cobranza real registrada</small>
          </div>

          <div class="summary-kpi">
            <span>Costo neto</span>
            <strong>${money(finanzas.costoNeto)}</strong>
            <small>Costo después de ajustes y devoluciones</small>
          </div>

          <div class="summary-kpi">
            <span>Utilidad</span>
            <strong>${money(finanzas.utilidad)}</strong>
            <small>Resultado actual del proyecto</small>
          </div>

          <div class="summary-kpi">
            <span>Por cobrar</span>
            <strong>${money(finanzas.porCobrar)}</strong>
            <small>Dinero aún no recuperado</small>
          </div>
        </div>
      </div>
    </section>

    <section class="kpis">
      <div class="kpi-card">
        <span>Monto contratado</span>
        <strong>${money(finanzas.ingresoEsperado)}</strong>
        <small>Meta / contrato / proyección</small>
      </div>
      <div class="kpi-card">
        <span>Cobrado</span>
        <strong>${money(finanzas.ingresos)}</strong>
        <small>Avance de cobro: ${percent(finanzas.avanceCobro)}</small>
      </div>
      <div class="kpi-card">
        <span>Costo neto</span>
        <strong>${money(finanzas.costoNeto)}</strong>
        <small>Costo neto acumulado del proyecto</small>
      </div>
      <div class="kpi-card">
        <span>Utilidad</span>
        <strong>${money(finanzas.utilidad)}</strong>
        <small>Margen: ${percent(finanzas.margen)}</small>
      </div>
      <div class="kpi-card">
        <span>Por cobrar</span>
        <strong>${money(finanzas.porCobrar)}</strong>
        <small>Recuperación pendiente</small>
      </div>
      <div class="kpi-card">
        <span>Vista</span>
        <strong>${movimientosFiltrados.length}</strong>
        <small>Movimientos considerados</small>
      </div>
    </section>

    <section class="layout">
      <div class="panel">
        <div class="eyebrow">Termómetro financiero</div>
        <h3>Comparativo visual</h3>
        <p class="sub">A una sola vista: cuánto entró, cuánto costó, cuánto dejó y cuánto sigue pendiente.</p>

        <div class="compare-bars">
          <div class="compare-item">
            <div class="bar-column">
              <div class="bar-fill-vertical bar-ingresos" style="height:${clamp((finanzas.ingresos / baseGrafica) * 100, 0, 100)}%;"></div>
            </div>
            <strong>Cobrado</strong>
            <small>${moneyCompact(finanzas.ingresos)}</small>
          </div>

          <div class="compare-item">
            <div class="bar-column">
              <div class="bar-fill-vertical bar-costos" style="height:${clamp((finanzas.costoNeto / baseGrafica) * 100, 0, 100)}%;"></div>
            </div>
            <strong>Costo neto</strong>
            <small>${moneyCompact(finanzas.costoNeto)}</small>
          </div>

          <div class="compare-item">
            <div class="bar-column">
              <div class="bar-fill-vertical bar-utilidad" style="height:${clamp((Math.abs(finanzas.utilidad) / baseGrafica) * 100, 0, 100)}%;"></div>
            </div>
            <strong>Utilidad</strong>
            <small>${moneyCompact(finanzas.utilidad)}</small>
          </div>

          <div class="compare-item">
            <div class="bar-column">
              <div class="bar-fill-vertical bar-cobrar" style="height:${clamp((finanzas.porCobrar / baseGrafica) * 100, 0, 100)}%;"></div>
            </div>
            <strong>Por cobrar</strong>
            <small>${moneyCompact(finanzas.porCobrar)}</small>
          </div>
        </div>
      </div>

      <div class="panel">
        <div class="eyebrow">En qué se está gastando el dinero</div>
        <h3>Desglose de costos</h3>
        <p class="sub">Top de categorías que más pesan dentro del costo total.</p>
        ${costoRows}
      </div>
    </section>

    <section class="ops">
      <div class="ops-card">
        <span>Unidades</span>
        <strong>${toNumber(finanzas.unidades).toLocaleString("es-MX")}</strong>
        <small>Costo por unidad: ${money(finanzas.costoPorUnidad)}</small>
      </div>
      <div class="ops-card">
        <span>Ingreso / unidad</span>
        <strong>${money(finanzas.ingresoPorUnidad)}</strong>
        <small>Utilidad por unidad: ${money(finanzas.utilidadPorUnidad)}</small>
      </div>
      <div class="ops-card">
        <span>Bolsa horas</span>
        <strong>${toNumber(finanzas.horasBolsa).toLocaleString("es-MX")} h</strong>
        <small>Costo hora promedio: ${money(finanzas.costoHoraPromedio)}</small>
      </div>
      <div class="ops-card">
        <span>Recurrente mensual</span>
        <strong>${money(finanzas.recurrenteMensual)}</strong>
        <small>Personal / operador / soporte</small>
      </div>
      <div class="ops-card">
        <span>Extraordinarios</span>
        <strong>${money(finanzas.extraordinarios)}</strong>
        <small>Daños, accesorios, urgencias</small>
      </div>
    </section>

    <section class="panel impact-panel">
      <div class="eyebrow">Movimientos con mayor impacto</div>
      <h3>Qué está moviendo el resultado</h3>
      <p class="sub">Los movimientos de mayor monto dentro de la vista actual.</p>

      <div class="impact-list">
        ${movimientosImpacto}
      </div>
    </section>

    <section class="footer-note">
      <strong>Vista aplicada:</strong> ${safeText(periodoVista)}
      ${filtrosActivosTexto ? " · " + safeText(filtrosActivosTexto) : ""}
      <br />
      <strong>Lectura ejecutiva:</strong> este reporte resume si el proyecto está recuperando la inversión,
      cuánto deja de utilidad y en qué rubros se concentra el gasto.
    </section>
  </div>

  <script>
    function descargarHtml() {
      var html = "<!doctype html>\n" + document.documentElement.outerHTML;
      var blob = new Blob([html], { type: "text/html;charset=utf-8" });
      var url = URL.createObjectURL(blob);
      var a = document.createElement("a");
      var name = document.title || "reporte-directivo";
      name = name
        .toLowerCase()
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-+|-+$/g, "");

      a.href = url;
      a.download = name + ".html";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);

      setTimeout(function () {
        URL.revokeObjectURL(url);
      }, 1000);
    }
  </script>
</body>
</html>`;
  }

  function descargarReporteDirectivo() {
    try {
      const html = construirReporteDirectivoHtml();
      const nombreProyecto = String(proyecto?.nombre || proyecto?.titulo || "proyecto")
        .toLowerCase()
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-+|-+$/g, "")
        .slice(0, 60) || "proyecto";

      const blob = new Blob([html], { type: "text/html;charset=utf-8" });
      const url = URL.createObjectURL(blob);

      const a = document.createElement("a");
      a.href = url;
      a.download = `reporte-directivo-finanzas-${nombreProyecto}-${Date.now()}.html`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);

      const preview = window.open("", "_blank", "noopener,noreferrer");
      if (preview) {
        preview.document.open();
        preview.document.write(html);
        preview.document.close();
      }

      setTimeout(() => URL.revokeObjectURL(url), 1500);
    } catch (error) {
      console.error("No fue posible generar el reporte directivo", error);
      alert("No fue posible generar el reporte directivo.");
    }
  }

  // PATCH FRONTEND CIERRE LOCAL V1
  const resumenMensual = useMemo(() => {
    const map = new Map();

    movimientos.forEach((mov) => {
      const periodo = mov.periodo || String(mov.fecha || "").slice(0, 7) || "SIN_PERIODO";

      if (!map.has(periodo)) {
        map.set(periodo, {
          periodo,
          ingresos: 0,
          costos: 0,
          ajustes: 0,
          utilidad: 0,
          margen: 0,
          movimientos: 0,
        });
      }

      const row = map.get(periodo);
      const importe = calcularImporte(mov, config);

      if (mov.tipo === "INGRESO") row.ingresos += importe;
      if (mov.tipo === "COSTO") row.costos += importe;
      if (mov.tipo === "AJUSTE") row.ajustes += importe;

      row.movimientos += 1;
    });

    return Array.from(map.values())
      .map((row) => {
        const utilidad = row.ingresos - row.costos + row.ajustes;
        return {
          ...row,
          utilidad,
          margen: row.ingresos > 0 ? (utilidad / row.ingresos) * 100 : 0,
        };
      })
      .sort((a, b) => String(b.periodo).localeCompare(String(a.periodo)));
  }, [movimientos, config]);

  function descargarMovimientosCsv() {
    try {
      const proyectoNombre = String(proyecto?.nombre || proyecto?.titulo || "proyecto")
        .toLowerCase()
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-+|-+$/g, "")
        .slice(0, 60) || "proyecto";

      const headers = [
        "proyectoId",
        "fecha",
        "periodo",
        "tipo",
        "naturaleza",
        "categoria",
        "concepto",
        "estado",
        "cantidad",
        "unidad",
        "precioUnitario",
        "monto",
        "proveedor",
        "responsable",
        "notas",
      ];

      const rows = movimientosFiltrados.map((mov) => [
        proyectoId,
        mov.fecha || "",
        mov.periodo || "",
        mov.tipo || "",
        mov.naturaleza || "",
        labelCategoria(mov.categoria),
        mov.concepto || "",
        mov.estado || "",
        toNumber(mov.cantidad),
        mov.unidad || "",
        toNumber(mov.precioUnitario),
        toNumber(mov.monto),
        mov.proveedor || "",
        mov.responsable || "",
        mov.notas || "",
      ]);

      const csv = [
        headers.map(csvValue).join(","),
        ...rows.map((row) => row.map(csvValue).join(",")),
      ].join("\n");

      const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8" });
      const url = URL.createObjectURL(blob);

      const a = document.createElement("a");
      a.href = url;
      a.download = "movimientos-financieros-" + proyectoNombre + "-" + shortDateTime() + ".csv";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);

      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (error) {
      console.error("No fue posible exportar movimientos CSV", error);
      alert("No fue posible exportar movimientos CSV.");
    }
  }

  function aplicarPresetMovimiento(preset) {
    setFormMovimiento((prev) => ({
      ...prev,
      ...preset,
      tipo: preset.tipo || prev.tipo,
      naturaleza: preset.naturaleza || prev.naturaleza,
      categoria: preset.categoria || prev.categoria,
      unidad: preset.unidad || prev.unidad,
      concepto: preset.concepto || prev.concepto,
    }));
  }

  const financeSourceLabel =
    financeSource === "GOOGLE_SHEETS"
      ? "Google Sheets · BANCARIO"
      : financeSource === "DYNAMO_FALLBACK"
        ? "DynamoDB · respaldo"
        : financeSource === "CACHE_LOCAL"
          ? "Cache local · sin conexión"
          : financeSource === "CARGANDO"
            ? "Consultando fuente..."
            : financeSource || "Backend";

  const financeSourceClass =
    financeSource === "GOOGLE_SHEETS"
      ? "google"
      : financeSource === "DYNAMO_FALLBACK"
        ? "dynamo"
        : financeSource === "CACHE_LOCAL"
          ? "warning"
          : "neutral";

  const financeSyncLabel =
    financeSyncAt
      ? new Date(financeSyncAt).toLocaleTimeString("es-MX", {
          hour: "2-digit",
          minute: "2-digit",
        })
      : "";

  const categoriasDisponibles =
    formMovimiento.tipo === "INGRESO" ? CATEGORIAS_INGRESO : CATEGORIAS_COSTO;

  return (
    <section className={visible ? "pcc-finance-board" : "pcc-finance-board pcc-hidden"}>
      <div className="pcc-section-title">
        <span>Finanzas</span>
        <strong>Motor financiero universal</strong>
      </div>

      <div className="pcc-finance-source-strip">
        <div className="pcc-finance-source-main">
          <span>Fuente financiera</span>

          <strong>
            <i className={"pcc-finance-source-dot " + financeSourceClass} />
            {financeSourceLabel}
          </strong>

          <small>
            {financeSource === "GOOGLE_SHEETS"
              ? "Información bancaria sincronizada desde Google Sheets."
              : financeSource === "DYNAMO_FALLBACK"
                ? "Google no tiene movimientos para este proyecto. Se conserva el respaldo de DynamoDB."
                : financeSource === "CACHE_LOCAL"
                  ? "No fue posible consultar el backend. Los datos mostrados pueden ser temporales."
                  : "Consultando fuente financiera..."}

            {financeSyncLabel
              ? " · Actualizado " + financeSyncLabel
              : ""}
          </small>

          {financeLoadError ? (
            <small className="pcc-finance-source-error">
              {financeLoadError}
            </small>
          ) : null}
        </div>

        <div className="pcc-finance-source-state">
          <span>SOLO LECTURA</span>
          <small>Captura oficial en Google Sheets</small>
        </div>
      </div>

      <div className="pcc-finance-compact-card">
        <div className="pcc-finance-compact-head">
          <div>
            <span>Estado financiero</span>
            <strong>{finanzas.estado}</strong>
            <small>{labelModelo(config.modeloFinanciero)} · {movimientos.length} movimientos</small>
          </div>

          <div className={"pcc-finance-compact-badge " + finanzas.estadoClase}>
            {percent(finanzas.margen)}
          </div>
        </div>

        <div className="pcc-finance-compact-kpis">
          <article>
            <span>Cobrado</span>
            <strong>{money(finanzas.ingresos)}</strong>
          </article>
          <article>
            <span>Costo neto</span>
            <strong>{money(finanzas.costoNeto)}</strong>
          </article>
          <article>
            <span>Utilidad</span>
            <strong>{money(finanzas.utilidad)}</strong>
          </article>
          <article>
            <span>Por cobrar</span>
            <strong>{money(finanzas.porCobrar)}</strong>
          </article>
        </div>

        <button
          type="button"
          className="pcc-finance-director-report-main"
          onClick={descargarReporteDirectivo}
        >
          Descargar reporte directivo PDF
        </button>

        <button
          type="button"
          className="pcc-finance-open-workspace secondary"
          onClick={() => setModalFinanzasAbierto(true)}
        >
          Abrir Finanzas completas
        </button>
      </div>

      {modalFinanzasAbierto && createPortal((
        <div className="pcc-finance-workspace-backdrop pcc-reference-backdrop">

          {/* PCC_FINANCE_REFERENCE_V4 */}
          {/* PCC_FINANCE_ONE_SCREEN_V4_2 */}

          <div
            className={
              "pcc-finance-workspace pcc-finance-reference-v4 finance-view-" +
              String(financeWorkspaceView || "RESUMEN").toLowerCase()
            }
          >

            {/* =================================================
                HEADER
                ================================================= */}

            <header className="pcc-ref-header">

              <div className="pcc-ref-header-main">

                <div className="pcc-ref-project">

                  <div className="pcc-ref-eyebrow">
                    
                    Workspace financiero
                  </div>

                  <h1>
                    {proyecto?.nombre ||
                      proyecto?.titulo ||
                      "Proyecto seleccionado"}
                  </h1>

                  <p>
                    Motor financiero conectado
                    <span>•</span>
                    <strong>
                      {labelModelo(config.modeloFinanciero)}
                    </strong>
                  </p>

                </div>

                <div className="pcc-ref-header-actions">

                  <button
                    type="button"
                    className="secondary"
                    onClick={abrirConfig}
                  >
                    Opciones
                  </button>

                  <button
                    type="button"
                    className="primary"
                    onClick={() => {
    setModalFinanzasAbierto(false);
    onWorkspaceClose?.();
  }}
                  >
                    Cerrar
                  </button>

                </div>

              </div>

              <nav
                className="pcc-ref-tabs"
                aria-label="Navegación financiera"
              >

                <button
                  type="button"
                  className={
                    financeWorkspaceView === "RESUMEN"
                      ? "active"
                      : ""
                  }
                  onClick={() =>
                    setFinanceWorkspaceView("RESUMEN")
                  }
                >
                  Resumen
                </button>

                <button
                  type="button"
                  className={
                    financeWorkspaceView === "MOVIMIENTOS"
                      ? "active"
                      : ""
                  }
                  onClick={() =>
                    setFinanceWorkspaceView("MOVIMIENTOS")
                  }
                >
                  Movimientos
                </button>

                <button
                  type="button"
                  className={
                    financeWorkspaceView === "HERRAMIENTAS"
                      ? "active"
                      : ""
                  }
                  onClick={() =>
                    setFinanceWorkspaceView("HERRAMIENTAS")
                  }
                >
                  Administración
                </button>

              </nav>

            </header>


            {/* =================================================
                RESUMEN
                ================================================= */}

            {financeWorkspaceView === "RESUMEN" && (

              <main className="pcc-ref-content">

                {/* ---------------------------------------------
                    CAPTURA + MODELO
                    --------------------------------------------- */}

                <section className="pcc-ref-top-grid">

                  <article className="pcc-ref-card pcc-ref-capture">

                    <div className="pcc-ref-card-title">

                      <div className="pcc-ref-title-with-icon">
                        

                        <div>
                          <strong>Captura rápida</strong>

                          <small>
                            Registra un nuevo movimiento
                            financiero en el proyecto.
                          </small>
                        </div>
                      </div>

                    </div>

                    <div className="pcc-ref-capture-row">

                      <label>

                        <span>Tipo de movimiento</span>

                        <select
                          value={financeCaptureType}
                          onChange={(e) =>
                            setFinanceCaptureType(
                              e.target.value
                            )
                          }
                        >
                          <option value="INGRESO">
                            Cobro / ingreso recibido
                          </option>

                          <option value="GASTO">
                            Costo real / gasto puntual
                          </option>

                          <option value="BOLSA">
                            Costo de bolsa de horas
                          </option>

                          <option value="MENSUAL">
                            Costo recurrente mensual
                          </option>
                        </select>

                      </label>

                      <button
                        type="button"
                        className="pcc-ref-register"
                        onClick={() => {

                          if (
                            financeCaptureType ===
                            "INGRESO"
                          ) {
                            abrirMovimiento("INGRESO");
                            return;
                          }

                          if (
                            financeCaptureType ===
                            "GASTO"
                          ) {
                            abrirMovimiento("COSTO");
                            return;
                          }

                          if (
                            financeCaptureType ===
                            "BOLSA"
                          ) {
                            abrirMovimiento(
                              "COSTO",
                              {
                                naturaleza:
                                  "BOLSA_HORAS",
                                categoria:
                                  "BOLSA_HORAS_DESARROLLO",
                                unidad: "hora",
                                concepto:
                                  "Costo de bolsa de horas de desarrollo",
                              }
                            );

                            return;
                          }

                          abrirMovimiento(
                            "COSTO",
                            {
                              naturaleza:
                                "RECURRENTE_MENSUAL",
                              categoria:
                                "OPERADOR",
                              unidad: "mes",
                              concepto:
                                "Costo recurrente mensual operativo",
                            }
                          );
                        }}
                      >
                        
                        Registrar movimiento
                      </button>

                    </div>

                  </article>


                  <article className="pcc-ref-card pcc-ref-model">

                    <div className="pcc-ref-model-head">

                      <div>

                        <span>
                          Modelo financiero
                        </span>

                        <strong>
                          {labelModelo(
                            config.modeloFinanciero
                          )}
                        </strong>

                      </div>

                      <div>

                        <span>
                          Monto contratado
                        </span>

                        <strong>
                          {money(
                            finanzas.ingresoEsperado
                          )}
                        </strong>

                      </div>

                    </div>

                    <div className="pcc-ref-model-meta">

                      <div>
                        <span>Unidad / servicio</span>

                        <strong>
                          {config.unidadCobro ||
                            "Servicio"}
                        </strong>
                      </div>

                      <div>
                        <span>Precio</span>

                        <strong>
                          {money(
                            config.precioUnitario
                          )}
                        </strong>
                      </div>

                      <div>
                        <span>Meta</span>

                        <strong>
                          {toNumber(
                            config.metaMaxima
                          ).toLocaleString(
                            "es-MX"
                          )}{" "}
                          unidad
                        </strong>
                      </div>

                      <div>
                        <span>IVA</span>

                        <strong>
                          {config.ivaIncluido
                            ? "Incluido"
                            : "No incluido"}
                        </strong>
                      </div>

                    </div>

                  </article>

                </section>


                {/* ---------------------------------------------
                    ESTADO
                    --------------------------------------------- */}

                <section className="pcc-ref-card pcc-ref-status">

                  <div className="pcc-ref-status-main">

                    <span>
                      Estado financiero
                    </span>

                    <div>

                      <strong>
                        {finanzas.estado}
                      </strong>

                      <em
                        className={
                          finanzas.estadoClase ===
                          "healthy"
                            ? "positive"
                            : "attention"
                        }
                      >
                        {finanzas.utilidad >= 0
                          ? "Positivo"
                          : "Atención"}
                      </em>

                    </div>

                  </div>

                  <div className="pcc-ref-status-margin">

                    <span>Margen</span>

                    <strong>
                      {percent(finanzas.margen)}
                    </strong>

                  </div>

                  <div className="pcc-ref-collection">

                    <div className="pcc-ref-collection-head">

                      <span>
                        Avance de cobro
                      </span>

                      <strong>
                        {percent(
                          finanzas.avanceCobro
                        )}
                      </strong>

                    </div>

                    <div className="pcc-ref-progress">

                      <div
                        style={{
                          width:
                            Math.max(
                              0,
                              Math.min(
                                100,
                                toNumber(
                                  finanzas.avanceCobro
                                )
                              )
                            ) + "%",
                        }}
                      />

                    </div>

                    <div className="pcc-ref-progress-meta">

                      <span>
                        Cobrado{" "}
                        {money(
                          finanzas.ingresos
                        )}
                      </span>

                      <span>
                        Meta{" "}
                        {money(
                          finanzas.ingresoEsperado
                        )}
                      </span>

                    </div>

                  </div>

                </section>


                {/* ---------------------------------------------
                    HERO KPI
                    --------------------------------------------- */}

                <section className="pcc-ref-hero-kpis">

                  <article className="warning">

                    <div className="pcc-ref-kpi-head">

                      <span>
                        <i />
                        Por cobrar
                      </span>

                      <small>
                        {finanzas.ingresoEsperado > 0
                          ? (
                              (
                                finanzas.porCobrar /
                                finanzas.ingresoEsperado
                              ) *
                              100
                            ).toFixed(0) + "% del total"
                          : "Sin meta"}
                      </small>

                    </div>

                    <strong>
                      {money(finanzas.porCobrar)}
                    </strong>

                    <p>
                      Saldo pendiente del monto contratado.
                    </p>

                  </article>


                  <article className="positive">

                    <div className="pcc-ref-kpi-head">

                      <span>
                        <i />
                        Utilidad actual
                      </span>

                      <small>
                        {finanzas.utilidad >= 0
                          ? "Positiva"
                          : "Negativa"}
                      </small>

                    </div>

                    <strong>
                      {money(finanzas.utilidad)}
                    </strong>

                    <p>
                      Ingresos cobrados menos lo
                      invertido a la fecha.
                    </p>

                  </article>


                  <article className="neutral">

                    <div className="pcc-ref-kpi-head">

                      <span>
                        <i />
                        Margen
                      </span>

                      <small>
                        Sobre ingresos
                      </small>

                    </div>

                    <strong>
                      {percent(finanzas.margen)}
                    </strong>

                    <p>
                      Rentabilidad del periodo
                      acumulado del proyecto.
                    </p>

                  </article>

                </section>


                {/* ---------------------------------------------
                    KPI SECUNDARIOS
                    --------------------------------------------- */}

                <section className="pcc-ref-secondary-kpis">

                  <article>
                    <span>Monto contratado</span>
                    <strong>
                      {money(
                        finanzas.ingresoEsperado
                      )}
                    </strong>
                    <small>
                      Monto formal contratado del proyecto
                    </small>
                  </article>

                  <article>
                    <span>Cobrado</span>
                    <strong>
                      {money(finanzas.ingresos)}
                    </strong>
                    <small>
                      Cobrado y conciliado
                    </small>
                  </article>

                  <article>
                    <span>Costo bruto</span>
                    <strong>
                      {money(finanzas.costoBruto)}
                    </strong>
                    <small>
                      Total de costos registrados
                    </small>
                  </article>

                  <article>
                    <span>Ajustes</span>
                    <strong>
                      {money(finanzas.ajustes)}
                    </strong>
                    <small>
                      Devoluciones y ajustes financieros
                    </small>
                  </article>

                  <article>
                    <span>Costo neto</span>
                    <strong>
                      {money(finanzas.costoNeto)}
                    </strong>
                    <small>
                      Costo efectivo después de ajustes
                    </small>
                  </article>

                  <article>
                    <span>ROI neto</span>
                    <strong>
                      {percent(finanzas.roi)}
                    </strong>
                    <small>
                      Utilidad sobre costo neto
                    </small>
                  </article>
                  {/* PCC_FIN_COST_CLASSIFICATION_V1_2_UI */}
                  <article>
                    <span>Directo general</span>
                    <strong>
                      {money(finanzas.costoDirectoGeneral)}
                    </strong>
                    <small>
                      Costo directo común
                    </small>
                  </article>

                  <article>
                    <span>Directo específico</span>
                    <strong>
                      {money(finanzas.costoDirectoEspecifico)}
                    </strong>
                    <small>
                      Costo atribuible al servicio
                    </small>
                  </article>

                  <article>
                    <span>Directo total</span>
                    <strong>
                      {money(finanzas.costoDirecto)}
                    </strong>
                    <small>
                      {percent(finanzas.porcentajeCostoDirecto)} del costo bruto
                    </small>
                  </article>

                  <article>
                    <span>Indirecto</span>
                    <strong>
                      {money(finanzas.costoIndirecto)}
                    </strong>
                    <small>
                      {percent(finanzas.porcentajeCostoIndirecto)} del costo bruto
                    </small>
                  </article>

                  <article>
                    <span>Unidades</span>
                    <strong>
                      {toNumber(
                        finanzas.unidades
                      ).toLocaleString(
                        "es-MX"
                      )}
                    </strong>
                    <small>
                      {finanzas.unidades > 0
                        ? "Unidades registradas"
                        : "Sin unidades registradas"}
                    </small>
                  </article>

                  <article>
                    <span>Costo unitario</span>
                    <strong>
                      {money(
                        finanzas.costoPorUnidad
                      )}
                    </strong>
                    <small>
                      Requiere unidades
                    </small>
                  </article>

                  <article>
                    <span>Horas hombre</span>
                    <strong>
                      {toNumber(
                        finanzas.horasBolsa
                      ).toLocaleString(
                        "es-MX"
                      )}{" "}
                      h
                    </strong>
                    <small>
                      Sin horas capturadas
                    </small>
                  </article>

                  <article>
                    <span>
                      Presupuesto mensual
                    </span>
                    <strong>
                      {money(
                        finanzas.recurrenteMensual
                      )}
                    </strong>
                    <small>
                      Personal y servicios
                    </small>
                  </article>

                  <article>
                    <span>
                      Extraordinarios
                    </span>
                    <strong>
                      {money(
                        finanzas.extraordinarios
                      )}
                    </strong>
                    <small>
                      Eventos fuera de plan
                    </small>
                  </article>

                </section>


                {/* ---------------------------------------------
                    EVOLUCION REAL + TABLA
                    --------------------------------------------- */}

                {(() => {

                  const allRows =
                    [...resumenMensual]
                      .reverse();

                  const take =
                    financeChartRange === "3M"
                      ? 3
                      : financeChartRange === "6M"
                        ? 6
                        : financeChartRange === "12M"
                          ? 12
                          : allRows.length;

                  const rows =
                    allRows.slice(
                      Math.max(
                        0,
                        allRows.length - take
                      )
                    );

                  const maxValue =
                    Math.max(
                      1,
                      ...rows.flatMap(
                        (row) => [
                          toNumber(row.ingresos),
                          toNumber(row.costos),
                          Math.max(
                            0,
                            toNumber(
                              row.utilidad
                            )
                          ),
                        ]
                      )
                    );

                  const W = 1200;
                  const H = 270;

                  const left = 64;
                  const right = 20;
                  const top = 20;
                  const bottom = 46;

                  const plotW =
                    W - left - right;

                  const plotH =
                    H - top - bottom;

                  const groupW =
                    rows.length > 0
                      ? plotW / rows.length
                      : plotW;

                  const barWidth =
                    Math.min(
                      24,
                      Math.max(
                        9,
                        groupW * 0.22
                      )
                    );

                  const yFor = (value) =>
                    top +
                    plotH -
                    (
                      Math.max(
                        0,
                        toNumber(value)
                      ) /
                      maxValue
                    ) *
                    plotH;

                  const xFor = (index) =>
                    left +
                    index * groupW +
                    groupW / 2;

                  const utilidadPoints =
                    rows
                      .map(
                        (row, index) =>
                          xFor(index) +
                          "," +
                          yFor(
                            row.utilidad
                          )
                      )
                      .join(" ");

                  const formatPeriodo = (
                    value
                  ) => {

                    const text =
                      String(value || "");

                    const match =
                      text.match(
                        /^(\d{4})-(\d{2})$/
                      );

                    if (!match) {
                      return text;
                    }

                    const meses = [
                      "",
                      "Ene",
                      "Feb",
                      "Mar",
                      "Abr",
                      "May",
                      "Jun",
                      "Jul",
                      "Ago",
                      "Sep",
                      "Oct",
                      "Nov",
                      "Dic",
                    ];

                    return (
                      meses[
                        Number(match[2])
                      ] +
                      " " +
                      match[1].slice(2)
                    );
                  };

                  const totalIngresos =
                    rows.reduce(
                      (sum, row) =>
                        sum +
                        toNumber(
                          row.ingresos
                        ),
                      0
                    );

                  const totalCostos =
                    rows.reduce(
                      (sum, row) =>
                        sum +
                        toNumber(
                          row.costos
                        ),
                      0
                    );

                  const totalUtilidad =
                    rows.reduce(
                      (sum, row) =>
                        sum +
                        toNumber(
                          row.utilidad
                        ),
                      0
                    );

                  const totalMovimientos =
                    rows.reduce(
                      (sum, row) =>
                        sum +
                        toNumber(
                          row.movimientos
                        ),
                      0
                    );

                  const totalMargen =
                    totalIngresos > 0
                      ? (
                          totalUtilidad /
                          totalIngresos
                        ) *
                        100
                      : 0;

                  return (
                    <section className="pcc-ref-chart-card">

                      <div className="pcc-ref-chart-head">

                        <div>
                          <strong>
                            Evolución financiera
                          </strong>

                          <small>
                            Ingresos, costos y
                            utilidad por periodo
                          </small>
                        </div>

                        <div className="pcc-ref-chart-controls">

                          <div className="pcc-ref-legend">

                            <span>
                              <i className="income" />
                              Ingresos
                            </span>

                            <span>
                              <i className="cost" />
                              Costos
                            </span>

                            <span>
                              <i className="profit" />
                              Utilidad
                            </span>

                          </div>

                          <div className="pcc-ref-range">

                            {[
                              "3M",
                              "6M",
                              "12M",
                              "TODO",
                            ].map(
                              (range) => (
                                <button
                                  key={range}
                                  type="button"
                                  className={
                                    financeChartRange ===
                                    range
                                      ? "active"
                                      : ""
                                  }
                                  onClick={() =>
                                    setFinanceChartRange(
                                      range
                                    )
                                  }
                                >
                                  {range ===
                                  "TODO"
                                    ? "Todo"
                                    : range}
                                </button>
                              )
                            )}

                          </div>

                        </div>

                      </div>


                      {rows.length === 0 ? (

                        <div className="pcc-ref-empty-chart">
                          Sin información mensual
                          suficiente para generar la
                          evolución.
                        </div>

                      ) : (

                        <div className="pcc-ref-chart-wrap">

                          <svg
                            viewBox={`0 0 ${W} ${H}`}
                            preserveAspectRatio="none"
                            className="pcc-ref-chart"
                            role="img"
                            aria-label="Evolución financiera"
                          >

                            {[0, 0.25, 0.5, 0.75, 1].map(
                              (ratio) => {

                                const y =
                                  top +
                                  plotH *
                                    ratio;

                                const value =
                                  maxValue *
                                  (1 - ratio);

                                return (
                                  <g
                                    key={
                                      ratio
                                    }
                                  >

                                    <line
                                      x1={left}
                                      x2={
                                        W -
                                        right
                                      }
                                      y1={y}
                                      y2={y}
                                      className="grid-line"
                                    />

                                    <text
                                      x={
                                        left -
                                        10
                                      }
                                      y={y + 4}
                                      textAnchor="end"
                                      className="axis-text"
                                    >
                                      {money(
                                        value
                                      )}
                                    </text>

                                  </g>
                                );
                              }
                            )}


                            {rows.map(
                              (
                                row,
                                index
                              ) => {

                                const center =
                                  xFor(index);

                                const ingresoY =
                                  yFor(
                                    row.ingresos
                                  );

                                const costoY =
                                  yFor(
                                    row.costos
                                  );

                                return (
                                  <g
                                    key={
                                      row.periodo
                                    }
                                  >

                                    <rect
                                      x={
                                        center -
                                        barWidth -
                                        2
                                      }
                                      y={
                                        ingresoY
                                      }
                                      width={
                                        barWidth
                                      }
                                      height={
                                        top +
                                        plotH -
                                        ingresoY
                                      }
                                      rx="3"
                                      className="bar-income"
                                    />

                                    <rect
                                      x={
                                        center +
                                        2
                                      }
                                      y={
                                        costoY
                                      }
                                      width={
                                        barWidth
                                      }
                                      height={
                                        top +
                                        plotH -
                                        costoY
                                      }
                                      rx="3"
                                      className="bar-cost"
                                    />

                                    <text
                                      x={
                                        center
                                      }
                                      y={
                                        H - 14
                                      }
                                      textAnchor="middle"
                                      className="month-text"
                                    >
                                      {formatPeriodo(
                                        row.periodo
                                      )}
                                    </text>

                                  </g>
                                );
                              }
                            )}


                            <polyline
                              points={
                                utilidadPoints
                              }
                              fill="none"
                              className="profit-line"
                            />


                            {rows.map(
                              (
                                row,
                                index
                              ) => (
                                <circle
                                  key={
                                    "profit-" +
                                    row.periodo
                                  }
                                  cx={
                                    xFor(
                                      index
                                    )
                                  }
                                  cy={
                                    yFor(
                                      row.utilidad
                                    )
                                  }
                                  r="4"
                                  className="profit-dot"
                                />
                              )
                            )}

                          </svg>

                        </div>
                      )}


                      <div className="pcc-ref-table-wrap">

                        <table className="pcc-ref-table">

                          <thead>
                            <tr>
                              <th>Periodo</th>
                              <th>Cobrado</th>
                              <th>Costo real</th>
                              <th>Utilidad</th>
                              <th>Margen</th>
                              <th>Movimientos</th>
                            </tr>
                          </thead>

                          <tbody>

                            {[...rows]
                              .reverse()
                              .map(
                                (row) => (
                                  <tr
                                    key={
                                      row.periodo
                                    }
                                  >
                                    <td>
                                      <strong>
                                        {formatPeriodo(
                                          row.periodo
                                        )}
                                      </strong>
                                    </td>

                                    <td>
                                      {money(
                                        row.ingresos
                                      )}
                                    </td>

                                    <td>
                                      {money(
                                        row.costos
                                      )}
                                    </td>

                                    <td className="positive-value">
                                      {money(
                                        row.utilidad
                                      )}
                                    </td>

                                    <td className="positive-value">
                                      {percent(
                                        row.margen
                                      )}
                                    </td>

                                    <td>
                                      {
                                        row.movimientos
                                      }
                                    </td>

                                  </tr>
                                )
                              )}

                          </tbody>

                          <tfoot>

                            <tr>

                              <td>
                                TOTAL{" "}
                                {financeChartRange ===
                                "TODO"
                                  ? ""
                                  : financeChartRange}
                              </td>

                              <td>
                                {money(
                                  totalIngresos
                                )}
                              </td>

                              <td>
                                {money(
                                  totalCostos
                                )}
                              </td>

                              <td className="positive-value">
                                {money(
                                  totalUtilidad
                                )}
                              </td>

                              <td className="positive-value">
                                {percent(
                                  totalMargen
                                )}
                              </td>

                              <td>
                                {
                                  totalMovimientos
                                }
                              </td>

                            </tr>

                          </tfoot>

                        </table>

                      </div>

                    </section>
                  );
                })()}

              </main>
            )}


            {/* =================================================
                MOVIMIENTOS
                ================================================= */}

            {financeWorkspaceView === "MOVIMIENTOS" && (

              <main className="pcc-ref-content">

                <section className="pcc-ref-card pcc-ref-section-card">

                  <div className="pcc-ref-section-heading">

                    <div>
                      <span>
                        Control financiero
                      </span>

                      <strong>
                        Movimientos e historial
                      </strong>
                    </div>

                    <button
                      type="button"
                      onClick={
                        limpiarFiltrosFinancieros
                      }
                      disabled={
                        !hayFiltrosActivos
                      }
                    >
                      Limpiar filtros
                    </button>

                  </div>


                  <div className="pcc-ref-filter-grid">

                    <label>
                      Periodo

                      <select
                        value={
                          filtrosFinancieros.periodo
                        }
                        onChange={(e) =>
                          actualizarFiltroFinanciero(
                            "periodo",
                            e.target.value
                          )
                        }
                      >
                        <option value="TODOS">
                          Todos
                        </option>

                        {periodosDisponibles.map(
                          (periodo) => (
                            <option
                              key={periodo}
                              value={periodo}
                            >
                              {periodo}
                            </option>
                          )
                        )}
                      </select>

                    </label>


                    <label>
                      Tipo

                      <select
                        value={
                          filtrosFinancieros.tipo
                        }
                        onChange={(e) =>
                          actualizarFiltroFinanciero(
                            "tipo",
                            e.target.value
                          )
                        }
                      >
                        <option value="TODOS">
                          Todos
                        </option>

                        {TIPOS_MOVIMIENTO.map(
                          (item) => (
                            <option
                              key={item}
                              value={item}
                            >
                              {item}
                            </option>
                          )
                        )}
                      </select>

                    </label>


                    <label>
                      Naturaleza

                      <select
                        value={
                          filtrosFinancieros.naturaleza
                        }
                        onChange={(e) =>
                          actualizarFiltroFinanciero(
                            "naturaleza",
                            e.target.value
                          )
                        }
                      >
                        <option value="TODAS">
                          Todas
                        </option>

                        {NATURALEZAS.map(
                          (item) => (
                            <option
                              key={item}
                              value={item}
                            >
                              {labelNaturaleza(
                                item
                              )}
                            </option>
                          )
                        )}
                      </select>

                    </label>


                    <label>
                      Estado

                      <select
                        value={
                          filtrosFinancieros.estado
                        }
                        onChange={(e) =>
                          actualizarFiltroFinanciero(
                            "estado",
                            e.target.value
                          )
                        }
                      >
                        <option value="TODOS">
                          Todos
                        </option>

                        {ESTADOS.map(
                          (item) => (
                            <option
                              key={item}
                              value={item}
                            >
                              {item}
                            </option>
                          )
                        )}
                      </select>

                    </label>

                  </div>

                </section>


                <section className="pcc-ref-card pcc-ref-section-card">

                  <div className="pcc-ref-section-heading">

                    <div>
                      <span>Costo real</span>
                      <strong>
                        Desglose por categoría
                      </strong>
                    </div>

                  </div>

                  {finanzas.desgloseCostos.length ===
                  0 ? (

                    <p className="pcc-ref-empty">
                      Sin costos registrados.
                    </p>

                  ) : (

                    finanzas.desgloseCostos.map(
                      (costo) => (

                        <div
                          key={costo.key}
                          className="pcc-ref-cost-row"
                        >
                          <span>
                            {costo.label}
                          </span>

                          <strong>
                            {money(costo.value)}
                          </strong>
                        </div>
                      )
                    )
                  )}

                </section>


                <section className="pcc-ref-card pcc-ref-section-card">

                  <div className="pcc-ref-section-heading">

                    <div>
                      <span>
                        Movimientos
                      </span>

                      <strong>
                        Registro financiero administrativo
                      </strong>
                    </div>

                    <small>
                      Mostrando{" "}
                      {movimientosFiltrados.length}{" "}
                      de {movimientos.length}
                    </small>

                  </div>


                  {/* PCC_FIN_COST_AUDIT_V5 */}

                  <div className="pcc-cost-audit-v5">

                    <div className="pcc-cost-audit-v5-head">

                      <div>
                        <span>VALIDACIÓN DIRECTIVA</span>

                        <strong>
                          Partidas que forman los costos
                        </strong>
                      </div>

                      <small>
                        Revisión de clasificación financiera
                      </small>

                    </div>


                    {/* PCC_FIN_COST_AUDIT_V5_1 */}

                    <div className="pcc-cost-reconcile-v51">

                      <div className="pcc-cost-reconcile-v51-copy">

                        <span>
                          CONCILIACIÓN DEL COSTO BRUTO
                        </span>

                        <strong>
                          Directos + indirectos = costo bruto
                        </strong>

                      </div>


                      <div className="pcc-cost-reconcile-v51-kpis">

                        <article className="direct">

                          <div>
                            <span>
                              DIRECTO TOTAL
                            </span>

                            <strong>
                              {money(
                                finanzas.costoDirecto
                              )}
                            </strong>
                          </div>

                          <em>
                            {percent(
                              finanzas.porcentajeCostoDirecto
                            )}
                          </em>

                          <small>
                            {money(
                              finanzas.costoDirectoGeneral
                            )}
                            {" + "}
                            {money(
                              finanzas.costoDirectoEspecifico
                            )}
                          </small>

                        </article>


                        <div className="pcc-cost-reconcile-v51-plus">
                          +
                        </div>


                        <article className="indirect">

                          <div>
                            <span>
                              INDIRECTO
                            </span>

                            <strong>
                              {money(
                                finanzas.costoIndirecto
                              )}
                            </strong>
                          </div>

                          <em>
                            {percent(
                              finanzas.porcentajeCostoIndirecto
                            )}
                          </em>

                          <small>
                            Costos compartidos, administrativos
                            y de soporte
                          </small>

                        </article>


                        <div className="pcc-cost-reconcile-v51-equal">
                          =
                        </div>


                        <article className="gross">

                          <div>
                            <span>
                              COSTO BRUTO
                            </span>

                            <strong>
                              {money(
                                finanzas.costoBruto
                              )}
                            </strong>
                          </div>

                          <em>
                            100%
                          </em>

                          <small>
                            Total de movimientos tipo COSTO
                          </small>

                        </article>

                      </div>

                    </div>

                    <div className="pcc-cost-audit-v5-grid">

                      {/* DIRECTO GENERAL */}

                      <section className="direct-general">

                        <header>

                          <div>
                            <span>DIRECTO GENERAL</span>

                            <strong>
                              {money(
                                finanzas.costoDirectoGeneral
                              )}
                            </strong>
                          </div>

                          <small>
                            {
                              movimientosFiltrados.filter(
                                (mov) =>
                                  mov.tipo === "COSTO" &&
                                  String(
                                    mov.clasificacionFinanciera || ""
                                  ).toUpperCase() ===
                                    "COSTO_DIRECTO_GENERAL"
                              ).length
                            } partidas
                          </small>

                        </header>


                        <div className="pcc-cost-audit-v5-list">

                          {
                            movimientosFiltrados
                              .filter(
                                (mov) =>
                                  mov.tipo === "COSTO" &&
                                  String(
                                    mov.clasificacionFinanciera || ""
                                  ).toUpperCase() ===
                                    "COSTO_DIRECTO_GENERAL"
                              )
                              .sort(
                                (a, b) =>
                                  Number(b.monto || 0) -
                                  Number(a.monto || 0)
                              )
                              .map((mov) => (

                                <div
                                  key={mov.id}
                                  className="pcc-cost-audit-v5-row"
                                >

                                  <div>

                                    <strong>
                                      {mov.concepto ||
                                        "Movimiento financiero"}
                                    </strong>

                                    <small>
                                      {mov.proveedor ||
                                        "Sin proveedor"}

                                      {mov.numeroFactura
                                        ? " · Factura " +
                                          mov.numeroFactura
                                        : ""}
                                    </small>

                                  </div>

                                  <span>
                                    {money(mov.monto)}
                                  </span>

                                </div>

                              ))
                          }

                          {
                            movimientosFiltrados.filter(
                              (mov) =>
                                mov.tipo === "COSTO" &&
                                String(
                                  mov.clasificacionFinanciera || ""
                                ).toUpperCase() ===
                                  "COSTO_DIRECTO_GENERAL"
                            ).length === 0 && (

                              <div className="pcc-cost-audit-v5-empty">
                                Sin partidas clasificadas.
                              </div>

                            )
                          }

                        </div>

                      </section>


                      {/* DIRECTO ESPECIFICO */}

                      <section className="direct-specific">

                        <header>

                          <div>
                            <span>
                              DIRECTO ESPECÍFICO
                            </span>

                            <strong>
                              {money(
                                finanzas.costoDirectoEspecifico
                              )}
                            </strong>
                          </div>

                          <small>
                            {
                              movimientosFiltrados.filter(
                                (mov) =>
                                  mov.tipo === "COSTO" &&
                                  String(
                                    mov.clasificacionFinanciera || ""
                                  ).toUpperCase() ===
                                    "COSTO_DIRECTO_ESPECIFICO"
                              ).length
                            } partidas
                          </small>

                        </header>


                        <div className="pcc-cost-audit-v5-list">

                          {
                            movimientosFiltrados
                              .filter(
                                (mov) =>
                                  mov.tipo === "COSTO" &&
                                  String(
                                    mov.clasificacionFinanciera || ""
                                  ).toUpperCase() ===
                                    "COSTO_DIRECTO_ESPECIFICO"
                              )
                              .sort(
                                (a, b) =>
                                  Number(b.monto || 0) -
                                  Number(a.monto || 0)
                              )
                              .map((mov) => (

                                <div
                                  key={mov.id}
                                  className="pcc-cost-audit-v5-row"
                                >

                                  <div>

                                    <strong>
                                      {mov.concepto ||
                                        "Movimiento financiero"}
                                    </strong>

                                    <small>
                                      {mov.proveedor ||
                                        "Sin proveedor"}

                                      {mov.numeroFactura
                                        ? " · Factura " +
                                          mov.numeroFactura
                                        : ""}
                                    </small>

                                  </div>

                                  <span>
                                    {money(mov.monto)}
                                  </span>

                                </div>

                              ))
                          }

                          {
                            movimientosFiltrados.filter(
                              (mov) =>
                                mov.tipo === "COSTO" &&
                                String(
                                  mov.clasificacionFinanciera || ""
                                ).toUpperCase() ===
                                  "COSTO_DIRECTO_ESPECIFICO"
                            ).length === 0 && (

                              <div className="pcc-cost-audit-v5-empty">
                                Sin partidas clasificadas.
                              </div>

                            )
                          }

                        </div>

                      </section>


                      {/* INDIRECTO */}

                      <section className="indirect">

                        <header>

                          <div>
                            <span>
                              INDIRECTO
                            </span>

                            <strong>
                              {money(
                                finanzas.costoIndirecto
                              )}
                            </strong>
                          </div>

                          <small>
                            {
                              movimientosFiltrados.filter(
                                (mov) =>
                                  mov.tipo === "COSTO" &&
                                  String(
                                    mov.clasificacionFinanciera || ""
                                  ).toUpperCase() ===
                                    "COSTO_INDIRECTO"
                              ).length
                            } partidas
                          </small>

                        </header>


                        <div className="pcc-cost-audit-v5-list">

                          {
                            movimientosFiltrados
                              .filter(
                                (mov) =>
                                  mov.tipo === "COSTO" &&
                                  String(
                                    mov.clasificacionFinanciera || ""
                                  ).toUpperCase() ===
                                    "COSTO_INDIRECTO"
                              )
                              .sort(
                                (a, b) =>
                                  Number(b.monto || 0) -
                                  Number(a.monto || 0)
                              )
                              .map((mov) => (

                                <div
                                  key={mov.id}
                                  className="pcc-cost-audit-v5-row"
                                >

                                  <div>

                                    <strong>
                                      {mov.concepto ||
                                        "Movimiento financiero"}
                                    </strong>

                                    <small>
                                      {mov.proveedor ||
                                        "Sin proveedor"}

                                      {mov.numeroFactura
                                        ? " · Factura " +
                                          mov.numeroFactura
                                        : ""}
                                    </small>

                                  </div>

                                  <span>
                                    {money(mov.monto)}
                                  </span>

                                </div>

                              ))
                          }

                          {
                            movimientosFiltrados.filter(
                              (mov) =>
                                mov.tipo === "COSTO" &&
                                String(
                                  mov.clasificacionFinanciera || ""
                                ).toUpperCase() ===
                                  "COSTO_INDIRECTO"
                            ).length === 0 && (

                              <div className="pcc-cost-audit-v5-empty">
                                Sin partidas clasificadas.
                              </div>

                            )
                          }

                        </div>

                      </section>

                    </div>

                  </div>
                  {movimientosFiltrados.length ===
                  0 ? (

                    <p className="pcc-ref-empty">
                      Sin movimientos para esta
                      vista.
                    </p>

                  ) : (

                    <div className="pcc-ref-table-wrap">

                      <table className="pcc-ref-table movements">

                        <thead>
                          <tr>
                            <th>Fecha</th>
                            <th>Periodo</th>
                            <th>Tipo</th>
                            <th>Categoría</th>
                            <th>Clasificación</th>
                            <th>Concepto</th>
                            <th>Estado</th>
                            <th>Importe</th>
                            <th>Acciones</th>
                          </tr>
                        </thead>

                        <tbody>

                          {movimientosFiltrados
                            .slice(0, 50)
                            .map(
                              (mov) => (

                                <tr key={mov.id}>

                                  <td>
                                    {mov.fecha ||
                                      "-"}
                                  </td>

                                  <td>
                                    {mov.periodo ||
                                      "-"}
                                  </td>

                                  <td>
                                    <span
                                      className={
                                        "pcc-ref-pill " +
                                        classMovimiento(
                                          mov.tipo
                                        )
                                      }
                                    >
                                      {mov.tipo}
                                    </span>
                                  </td>

                                  <td>
                                    {labelCategoria(
                                      mov.categoria
                                    )}
                                  </td>

                                  <td>
                                    {mov.tipo === "COSTO" ? (
                                      <span
                                        className={
                                          "pcc-fin-classification " +
                                          (
                                            String(
                                              mov.clasificacionFinanciera || ""
                                            ).toUpperCase() ===
                                            "COSTO_DIRECTO_GENERAL"
                                              ? "directo-general"
                                              : String(
                                                  mov.clasificacionFinanciera || ""
                                                ).toUpperCase() ===
                                                "COSTO_DIRECTO_ESPECIFICO"
                                                ? "directo-especifico"
                                                : String(
                                                    mov.clasificacionFinanciera || ""
                                                  ).toUpperCase() ===
                                                  "COSTO_INDIRECTO"
                                                  ? "indirecto"
                                                  : "sin-clasificar"
                                          )
                                        }
                                      >
                                        {
                                          String(
                                            mov.clasificacionFinanciera || ""
                                          ).toUpperCase() ===
                                          "COSTO_DIRECTO_GENERAL"
                                            ? "Directo general"
                                            : String(
                                                mov.clasificacionFinanciera || ""
                                              ).toUpperCase() ===
                                              "COSTO_DIRECTO_ESPECIFICO"
                                              ? "Directo específico"
                                              : String(
                                                  mov.clasificacionFinanciera || ""
                                                ).toUpperCase() ===
                                                "COSTO_INDIRECTO"
                                                ? "Indirecto"
                                                : "Sin clasificar"
                                        }
                                      </span>
                                    ) : (
                                      <span className="pcc-fin-classification neutral">
                                        {
                                          String(
                                            mov.clasificacionFinanciera || ""
                                          ).toUpperCase() ===
                                          "INGRESO_VENTA"
                                            ? "Ingreso / venta"
                                            : String(
                                                mov.clasificacionFinanciera || ""
                                              ).toUpperCase() ===
                                              "FINANCIAMIENTO_CAPITAL"
                                              ? "Financiamiento / capital"
                                              : "—"
                                        }
                                      </span>
                                    )}
                                  </td>
                                  <td className="concept">
                                    <strong>
                                      {
                                        mov.concepto
                                      }
                                    </strong>

                                    <small>
                                      {mov.proveedor ||
                                        ""}
                                    </small>
                                  </td>

                                  <td>
                                    {mov.estado ||
                                      "-"}
                                  </td>

                                  <td className="amount">
                                    {money(
                                      mov.monto
                                    )}
                                  </td>

                                  <td>

                                    <div className="pcc-ref-row-actions">

                                      <button
                                        type="button"
                                        onClick={() =>
                                          abrirEditarMovimiento(
                                            mov
                                          )
                                        }
                                      >
                                        Editar
                                      </button>

                                      <button
                                        type="button"
                                        onClick={() =>
                                          eliminarMovimiento(
                                            mov.id
                                          )
                                        }
                                      >
                                        Eliminar
                                      </button>

                                    </div>

                                  </td>

                                </tr>
                              )
                            )}

                        </tbody>

                      </table>

                    </div>
                  )}

                </section>

              </main>
            )}


            {/* =================================================
                ADMINISTRACION
                ================================================= */}

            {financeWorkspaceView ===
              "HERRAMIENTAS" && (

              <main className="pcc-ref-content">

                <section className="pcc-ref-admin-grid">

                  <article className="pcc-ref-card pcc-ref-admin-card">

                    <span>
                      Modelo financiero
                    </span>

                    <strong>
                      Configuración del proyecto
                    </strong>

                    <p>
                      Modelo de cobro, contrato,
                      unidad, precio, meta e IVA.
                    </p>

                    <button
                      type="button"
                      onClick={abrirConfig}
                    >
                      Editar modelo financiero
                    </button>

                  </article>


                  <article className="pcc-ref-card pcc-ref-admin-card">

                    <span>
                      Dirección
                    </span>

                    <strong>
                      Reporte ejecutivo
                    </strong>

                    <p>
                      Genera el reporte financiero
                      directivo del proyecto.
                    </p>

                    <button
                      type="button"
                      onClick={
                        descargarReporteDirectivo
                      }
                    >
                      Descargar PDF
                    </button>

                  </article>


                  <article className="pcc-ref-card pcc-ref-admin-card">

                    <span>
                      Datos
                    </span>

                    <strong>
                      Exportar movimientos
                    </strong>

                    <p>
                      Descarga el registro
                      financiero para análisis
                      externo.
                    </p>

                    <button
                      type="button"
                      onClick={
                        descargarMovimientosCsv
                      }
                    >
                      Exportar CSV
                    </button>

                  </article>

                </section>

              </main>
            )}

          </div>

        </div>
      ), document.body)}

      <div className="pcc-finance-note compact-note">
        <strong>Regla enterprise:</strong> el capturista no debe detenerse si cambia el esquema. Todo se registra como ingreso, costo o ajuste.
      </div>

      {modalConfigAbierto && createPortal((
        <div className="pcc-finance-modal-backdrop">
          <div className="pcc-finance-modal">
            <div className="pcc-finance-modal-head">
              <div>
                <span>Modelo financiero</span>
                <strong>Configuracion universal del proyecto</strong>
              </div>
              <button type="button" onClick={() => setModalConfigAbierto(false)}>
                x
              </button>
            </div>

            <div className="pcc-finance-form-grid">
              <label>
                Modelo financiero
                <select
                  value={formConfig.modeloFinanciero}
                  onChange={(e) => actualizarConfig("modeloFinanciero", e.target.value)}
                >
                  {MODELOS_FINANCIEROS.map((item) => (
                    <option key={item} value={item}>{labelModelo(item)}</option>
                  ))}
                </select>
              </label>

              <label>
                Unidad de cobro
                <input
                  value={formConfig.unidadCobro}
                  onChange={(e) => actualizarConfig("unidadCobro", e.target.value)}
                  placeholder="licencia, tramite, servicio, mes"
                />
              </label>

              <label>
                Precio unitario
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={formConfig.precioUnitario}
                  onChange={(e) => actualizarConfig("precioUnitario", e.target.value)}
                  placeholder="0.00"
                />
              </label>

              <label>
                IVA incluido
                <select
                  value={String(formConfig.ivaIncluido)}
                  onChange={(e) => actualizarConfig("ivaIncluido", e.target.value)}
                >
                  <option value="true">Si</option>
                  <option value="false">No</option>
                </select>
              </label>

              <label>
                Monto contrato
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={formConfig.montoContrato}
                  onChange={(e) => actualizarConfig("montoContrato", e.target.value)}
                  placeholder="0.00"
                />
              </label>

              <label>
                Mensualidad
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={formConfig.mensualidad}
                  onChange={(e) => actualizarConfig("mensualidad", e.target.value)}
                  placeholder="0.00"
                />
              </label>

              <label>
                Meta maxima
                <input
                  type="number"
                  min="0"
                  step="1"
                  value={formConfig.metaMaxima}
                  onChange={(e) => actualizarConfig("metaMaxima", e.target.value)}
                  placeholder="250000"
                />
              </label>

              <label className="wide">
                Notas comerciales
                <textarea
                  value={formConfig.notasComerciales}
                  onChange={(e) => actualizarConfig("notasComerciales", e.target.value)}
                  placeholder="Reglas de facturacion, cobro, alcance, excepciones o modalidad operativa."
                />
              </label>
            </div>

            <div className="pcc-finance-modal-actions">
              <button type="button" onClick={() => setModalConfigAbierto(false)}>
                Cancelar
              </button>
              <button type="button" className="primary" onClick={guardarConfig}>
                Guardar modelo
              </button>
            </div>
          </div>
        </div>
      ), document.body)}

      {modalMovimientoAbierto && createPortal((

        <div className="pcc-finance-modal-backdrop pcc-movement-v2-backdrop">

          {/* PCC_FINANCE_MOVEMENT_MODAL_V2 */}

          <div className="pcc-movement-v2-modal">

            {/* =================================================
                HEADER
                ================================================= */}

            <header className="pcc-movement-v2-header">

              <div>

                <span className="pcc-movement-v2-eyebrow">
                  <i />
                  Movimiento financiero
                </span>

                <h2>
                  {movimientoEditando
                    ? "Editar movimiento"
                    : formMovimiento.tipo === "INGRESO"
                      ? "Registrar ingreso"
                      : formMovimiento.naturaleza === "BOLSA_HORAS"
                        ? "Registrar bolsa de horas"
                        : formMovimiento.naturaleza === "RECURRENTE_MENSUAL"
                          ? "Registrar costo mensual"
                          : "Registrar costo real / gasto puntual"}
                </h2>

                <p>
                  {formMovimiento.tipo === "INGRESO"
                    ? "Captura un ingreso, pago o cobranza asociado al proyecto."
                    : formMovimiento.naturaleza === "BOLSA_HORAS"
                      ? "Registra horas, costo y periodo de la bolsa de trabajo."
                      : formMovimiento.naturaleza === "RECURRENTE_MENSUAL"
                        ? "Registra un costo recurrente asociado al proyecto."
                        : "Registra el monto real pagado o invertido en este proyecto."}
                </p>

              </div>

              <button
                type="button"
                className="pcc-movement-v2-close"
                onClick={cerrarModalMovimiento}
                aria-label="Cerrar"
              >
                ×
              </button>

            </header>


            {/* =================================================
                BODY SCROLL
                ================================================= */}

            <div className="pcc-movement-v2-body">


              {/* =================================================
                  PRESETS
                  ================================================= */}

              <section className="pcc-movement-v2-section presets">

                <div className="pcc-movement-v2-section-title">
                  <strong>Atajos de captura</strong>

                  <small>
                    Selecciona una plantilla para completar automáticamente
                    los campos frecuentes.
                  </small>
                </div>

                <div className="pcc-movement-v2-presets">

                  <button
                    type="button"
                    className={
                      formMovimiento.categoria === "VIATICOS"
                        ? "active"
                        : ""
                    }
                    onClick={() =>
                      aplicarPresetMovimiento({
                        tipo: "COSTO",
                        naturaleza: "UNICO",
                        categoria: "VIATICOS",
                        unidad: "servicio",
                        concepto: "Viáticos del proyecto",
                      })
                    }
                  >
                    Viáticos
                  </button>

                  <button
                    type="button"
                    className={
                      formMovimiento.categoria === "ACCESORIOS"
                        ? "active"
                        : ""
                    }
                    onClick={() =>
                      aplicarPresetMovimiento({
                        tipo: "COSTO",
                        naturaleza: "EXTRAORDINARIO",
                        categoria: "ACCESORIOS",
                        unidad: "pieza",
                        concepto: "Accesorios / compra urgente",
                      })
                    }
                  >
                    Accesorios
                  </button>

                  <button
                    type="button"
                    className={
                      formMovimiento.categoria === "REFACCIONES"
                        ? "active"
                        : ""
                    }
                    onClick={() =>
                      aplicarPresetMovimiento({
                        tipo: "COSTO",
                        naturaleza: "EXTRAORDINARIO",
                        categoria: "REFACCIONES",
                        unidad: "pieza",
                        concepto: "Refacciones / reparación",
                      })
                    }
                  >
                    Refacciones
                  </button>

                  <button
                    type="button"
                    className={
                      formMovimiento.categoria === "OPERADOR" &&
                      formMovimiento.naturaleza === "RECURRENTE_MENSUAL"
                        ? "active"
                        : ""
                    }
                    onClick={() =>
                      aplicarPresetMovimiento({
                        tipo: "COSTO",
                        naturaleza: "RECURRENTE_MENSUAL",
                        categoria: "OPERADOR",
                        unidad: "mes",
                        concepto: "Operador mensual",
                      })
                    }
                  >
                    Operador mensual
                  </button>

                  <button
                    type="button"
                    className={
                      formMovimiento.categoria === "SOPORTE" &&
                      formMovimiento.naturaleza === "RECURRENTE_MENSUAL"
                        ? "active"
                        : ""
                    }
                    onClick={() =>
                      aplicarPresetMovimiento({
                        tipo: "COSTO",
                        naturaleza: "RECURRENTE_MENSUAL",
                        categoria: "SOPORTE",
                        unidad: "mes",
                        concepto: "Soporte mensual",
                      })
                    }
                  >
                    Soporte mensual
                  </button>

                  <button
                    type="button"
                    className={
                      formMovimiento.categoria === "CORTE_PRODUCCION"
                        ? "active"
                        : ""
                    }
                    onClick={() =>
                      aplicarPresetMovimiento({
                        tipo: "INGRESO",
                        naturaleza: "POR_UNIDAD",
                        categoria: "CORTE_PRODUCCION",
                        unidad: config.unidadCobro || "licencia",
                        precioUnitario: config.precioUnitario || "",
                        concepto: "Corte de producción",
                      })
                    }
                  >
                    Cobro producción
                  </button>

                </div>

              </section>


              {/* =================================================
                  INFORMACION PRINCIPAL
                  ================================================= */}

              <section className="pcc-movement-v2-section">

                <div className="pcc-movement-v2-section-title simple">
                  <strong>Información principal</strong>
                </div>

                <div className="pcc-movement-v2-grid">

                  <label>
                    <span>Tipo</span>

                    <select
                      value={formMovimiento.tipo}
                      onChange={(e) =>
                        actualizarMovimiento(
                          "tipo",
                          e.target.value
                        )
                      }
                    >
                      {TIPOS_MOVIMIENTO.map((item) => (
                        <option key={item} value={item}>
                          {item}
                        </option>
                      ))}
                    </select>
                  </label>


                  <label>
                    <span>Naturaleza</span>

                    <select
                      value={formMovimiento.naturaleza}
                      onChange={(e) =>
                        actualizarMovimiento(
                          "naturaleza",
                          e.target.value
                        )
                      }
                    >
                      {NATURALEZAS.map((item) => (
                        <option key={item} value={item}>
                          {labelNaturaleza(item)}
                        </option>
                      ))}
                    </select>
                  </label>


                  <label>
                    <span>Categoría</span>

                    <select
                      value={formMovimiento.categoria}
                      onChange={(e) =>
                        actualizarMovimiento(
                          "categoria",
                          e.target.value
                        )
                      }
                    >
                      {categoriasDisponibles.map((item) => (
                        <option key={item} value={item}>
                          {labelCategoria(item)}
                        </option>
                      ))}
                    </select>
                  </label>


                  <label>
                    <span>Periodo</span>

                    <input
                      type="month"
                      value={formMovimiento.periodo}
                      onChange={(e) =>
                        actualizarMovimiento(
                          "periodo",
                          e.target.value
                        )
                      }
                    />
                  </label>


                  <label>
                    <span>Fecha</span>

                    <input
                      type="date"
                      value={formMovimiento.fecha}
                      onChange={(e) =>
                        actualizarMovimiento(
                          "fecha",
                          e.target.value
                        )
                      }
                    />
                  </label>


                                {!isDirectMoneyMovement(formMovimiento) && (
                <label>
                  Cantidad
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={formMovimiento.cantidad}
                    onChange={(e) =>
                      actualizarMovimiento(
                        "cantidad",
                        e.target.value
                      )
                    }
                    placeholder="100"
                  />
                </label>
              )}


                                {!isDirectMoneyMovement(formMovimiento) && (
                <label>
                  Unidad
                  <select
                    value={formMovimiento.unidad}
                    onChange={(e) =>
                      actualizarMovimiento(
                        "unidad",
                        e.target.value
                      )
                    }
                  >
                    {UNIDADES.map((item) => (
                      <option
                        key={item}
                        value={item}
                      >
                        {item}
                      </option>
                    ))}
                  </select>
                </label>
              )}


                                {!isDirectMoneyMovement(formMovimiento) && (
                <label>
                  Precio / costo unitario
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={formMovimiento.precioUnitario}
                    onChange={(e) =>
                      actualizarMovimiento(
                        "precioUnitario",
                        e.target.value
                      )
                    }
                    placeholder="800.00"
                  />
                </label>
              )}


                                <label
                className={
                  isDirectMoneyMovement(formMovimiento)
                    ? "wide pcc-direct-money-field"
                    : ""
                }
              >
                {isDirectMoneyMovement(formMovimiento)
                  ? (
                      formMovimiento.tipo === "INGRESO"
                        ? "Monto total de cobro *"
                        : "Monto total del costo *"
                    )
                  : "Monto total"}

                <div className="pcc-direct-money-input-wrap">

                  {isDirectMoneyMovement(formMovimiento) && (
                    <span className="pcc-direct-money-symbol">
                      $
                    </span>
                  )}

                  <input
                    type={
                      isDirectMoneyMovement(formMovimiento)
                        ? "text"
                        : "number"
                    }
                    inputMode="decimal"
                    autoComplete="off"
                    min={
                      isDirectMoneyMovement(formMovimiento)
                        ? undefined
                        : "0"
                    }
                    step={
                      isDirectMoneyMovement(formMovimiento)
                        ? undefined
                        : "0.01"
                    }
                    value={
                      isDirectMoneyMovement(formMovimiento)
                        ? formatDirectMoney(
                            formMovimiento.monto
                          )
                        : formMovimiento.monto
                    }
                    onChange={(e) =>
                      actualizarMovimiento(
                        "monto",
                        isDirectMoneyMovement(formMovimiento)
                          ? normalizeDirectMoney(
                              e.target.value
                            )
                          : e.target.value
                      )
                    }
                    placeholder={
                      isDirectMoneyMovement(formMovimiento)
                        ? "30,000,000.00"
                        : "80000.00"
                    }
                  />

                  {isDirectMoneyMovement(formMovimiento) && (
                    <span className="pcc-direct-money-currency">
                      MXN
                    </span>
                  )}

                </div>

                {isDirectMoneyMovement(formMovimiento) &&
                  toNumber(formMovimiento.monto) > 0 && (
                    <div className="pcc-direct-money-words">

                      <span>
                        Cantidad en letras
                      </span>

                      <strong>
                        {directMoneyToWordsMx(
                          formMovimiento.monto
                        )}
                      </strong>

                    </div>
                  )}
              </label>


                  <label>
                    <span>Estado</span>

                    <select
                      value={formMovimiento.estado}
                      onChange={(e) =>
                        actualizarMovimiento(
                          "estado",
                          e.target.value
                        )
                      }
                    >
                      {ESTADOS.map((item) => (
                        <option key={item} value={item}>
                          {item}
                        </option>
                      ))}
                    </select>
                  </label>

                </div>

              </section>


              {/* =================================================
                  DETALLE
                  ================================================= */}

              <section className="pcc-movement-v2-section">

                <div className="pcc-movement-v2-section-title simple">
                  <strong>Detalle del movimiento</strong>
                </div>

                <div className="pcc-movement-v2-grid">

                  <label className="wide">
                    <span>
                      Concepto <em>*</em>
                    </span>

                    <input
                      value={formMovimiento.concepto}
                      onChange={(e) =>
                        actualizarMovimiento(
                          "concepto",
                          e.target.value
                        )
                      }
                      placeholder="Describe brevemente el movimiento financiero"
                    />
                  </label>


                                    {/* PCC_FIN_METADATA_V1 */}

                  <label>
                    <span>Número de factura</span>

                    <input
                      value={formMovimiento.numeroFactura}
                      onChange={(e) =>
                        actualizarMovimiento(
                          "numeroFactura",
                          e.target.value
                        )
                      }
                      placeholder="Ej. FT. A-09243 / INVOICE 1934"
                    />
                  </label>


                  <label>
                    <span>Clasificación financiera</span>

                    <select
                      value={formMovimiento.clasificacionFinanciera}
                      onChange={(e) =>
                        actualizarMovimiento(
                          "clasificacionFinanciera",
                          e.target.value
                        )
                      }
                    >
                      <option value="">Sin clasificar</option>

                      <option value="COSTO_DIRECTO_GENERAL">
                        Costo directo general
                      </option>

                      <option value="COSTO_DIRECTO_ESPECIFICO">
                        Costo directo específico
                      </option>

                      <option value="COSTO_INDIRECTO">
                        Costo indirecto
                      </option>

                      <option value="FINANCIAMIENTO_CAPITAL">
                        Financiamiento / capital
                      </option>

                      <option value="INGRESO_VENTA">
                        Ingreso / venta
                      </option>
                    </select>
                  </label>


                  <label className="wide">
                    <span>Descripción / referencia bancaria</span>

                    <textarea
                      value={formMovimiento.descripcionBancaria}
                      onChange={(e) =>
                        actualizarMovimiento(
                          "descripcionBancaria",
                          e.target.value
                        )
                      }
                      placeholder="Ej. SPEI enviado, depósito recibido, referencia bancaria o descripción del estado de cuenta"
                    />
                  </label>

<label>
                    <span>Proveedor / persona</span>

                    <input
                      value={formMovimiento.proveedor}
                      onChange={(e) =>
                        actualizarMovimiento(
                          "proveedor",
                          e.target.value
                        )
                      }
                      placeholder="Nombre del proveedor, empleado o tercero"
                    />
                  </label>


                  <label>
                    <span>Responsable interno</span>

                    <input
                      value={formMovimiento.responsable}
                      onChange={(e) =>
                        actualizarMovimiento(
                          "responsable",
                          e.target.value
                        )
                      }
                      placeholder="Responsable del proyecto"
                    />
                  </label>


                  <div className="pcc-movement-v2-iva wide">

                    <div>
                      <strong>IVA incluido</strong>

                      <small>
                        Indica si el importe capturado ya considera impuestos.
                      </small>
                    </div>

                    <div className="pcc-movement-v2-toggle">

                      <button
                        type="button"
                        className={
                          String(formMovimiento.ivaIncluido) === "true"
                            ? "active"
                            : ""
                        }
                        onClick={() =>
                          actualizarMovimiento(
                            "ivaIncluido",
                            "true"
                          )
                        }
                      >
                        Sí
                      </button>

                      <button
                        type="button"
                        className={
                          String(formMovimiento.ivaIncluido) === "false"
                            ? "active"
                            : ""
                        }
                        onClick={() =>
                          actualizarMovimiento(
                            "ivaIncluido",
                            "false"
                          )
                        }
                      >
                        No
                      </button>

                    </div>

                  </div>

                </div>

              </section>


              {/* =================================================
                  NOTAS
                  ================================================= */}

              <section className="pcc-movement-v2-section">

                <div className="pcc-movement-v2-section-title simple">
                  <strong>Notas y evidencia</strong>
                </div>

                <label className="pcc-movement-v2-notes">

                  <span>Notas</span>

                  <textarea
                    value={formMovimiento.notas}
                    onChange={(e) =>
                      actualizarMovimiento(
                        "notas",
                        e.target.value
                      )
                    }
                    placeholder="Notas, evidencia pendiente, detalle operativo, factura, comprobación o condición especial."
                  />

                </label>

                <p className="pcc-movement-v2-help">
                  Puedes agregar factura, comprobación o evidencia posteriormente.
                </p>

              </section>


              {/* =================================================
                  IMPORTE
                  ================================================= */}

              <section className="pcc-movement-v2-total">

                <div>

                  <span>Importe calculado</span>

                  <strong>
                    {money(
                      calcularImporte(
                        formMovimiento,
                        config
                      )
                    )}
                  </strong>

                  {toNumber(formMovimiento.cantidad) > 0 &&
                   toNumber(formMovimiento.precioUnitario) > 0 && (

                    <small>
                      {toNumber(formMovimiento.cantidad).toLocaleString("es-MX")}
                      {" × "}
                      {money(formMovimiento.precioUnitario)}
                      {" = "}
                      {money(
                        toNumber(formMovimiento.cantidad) *
                        toNumber(formMovimiento.precioUnitario)
                      )}
                      {" · Cálculo automático"}
                    </small>

                  )}

                </div>

                <p>
                  Si capturas el monto total, se utilizará ese importe.
                  Si capturas cantidad y precio unitario, se calculará
                  automáticamente.
                </p>

              </section>

            </div>


            {/* =================================================
                FOOTER
                ================================================= */}

            <footer className="pcc-movement-v2-footer">

              <p>
                Los campos obligatorios deben completarse antes de guardar.
              </p>

              <div>

                <button
                  type="button"
                  className="secondary"
                  onClick={cerrarModalMovimiento}
                >
                  Cancelar
                </button>

                <button
                  type="button"
                  className="primary"
                  onClick={guardarMovimiento}
                >
                  {movimientoEditando
                    ? "Actualizar movimiento"
                    : "Guardar movimiento"}
                </button>

              </div>

            </footer>

          </div>

        </div>

      ), document.body)}
    </section>
  );
}
